import { useImportFileTransfer } from '../context/ImportFileTransferContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { uppercaseLabel } from '../styles/typography';
import { appBarActionButtonSx, appBarDividerSx, buildMsiAppBarStyles } from '../styles/msiAppBarStyles';
import { USER_SETTINGS_OPEN_EVENT } from '../utils/securityResolutionEvents';
import {
  announceContextRefreshApproval,
  applyStagedContextRefresh,
} from '../utils/watchlistContextRefreshApproval';
import { useSecurityOperationContext } from '../context/SecurityOperationContext';
import { selectFactorVendorReviews } from '../utils/factorReviewVisibility';
import {
  factorVendorSwitchingText,
  factorVendorSwitchWaitingMessage,
  isFactorVendorSwitchRunning,
} from '../context/factorDataSyncPolicy';
import {
  OVERRIDABLE_TOKENS,
  THEME_LIST,
  getThemeColor,
  getAdminAccentColor,
} from '../styles/themeTokens';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  Popover,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { performLogout } from '../utils/auth';
import ResetPasswordModal from './ResetPasswordModal';
import SignInMethodsSettings from './SignInMethodsSettings';
import NotificationBell from './NotificationBell';
import LlmSettingsDialog from './watchlist_news/LlmSettingsDialog';
import IdioVolCompareTool from './IdioVolCompareTool';
import PortfolioIngestPanel from './PortfolioIngestPanel';
import { dispatchPortfolioImportApplied } from '../utils/portfolioImportEvents';
import PortfolioUploader from './PortfolioUploader';
import FactorDataStatusControl from './dashboard/components/FactorDataStatusControl';
import FactorDatasetSelector from './dashboard/components/FactorDatasetSelector';
import FactorVendorSwitchConfirmDialog from './FactorVendorSwitchConfirmDialog';
import DataContextLineageSettings from './DataContextLineageSettings';
import IbkrConnectCard from './IbkrConnectCard';
import LinkedExcelWatchlistCard from './LinkedExcelWatchlistCard';
import ExecutionDataSourcePanel from './ExecutionDataSourcePanel';
import AdminUsersPanel from './admin/AdminUsersPanel';
import CustomerOrganizationsPanel from './admin/CustomerOrganizationsPanel';
import FactorDataMonitoringPanel from './admin/FactorDataMonitoringPanel';
import SystemCredentialsPanel from './admin/SystemCredentialsPanel';
import MarketCalendarPanel from './admin/MarketCalendarPanel';
import RecentPerformanceChart from './RecentPerformanceChart';
import WatchlistSecurityStatusTable from './WatchlistSecurityStatusTable';
import SecurityOperationProgress from './watchlist_table/SecurityOperationProgress';
import { useAppContext } from '../context/AppContext';
import { createWatchlistReviewNavigationState } from '../hooks/useWatchlistReviewNavigationHandoff';
import FactorContextReviewNotice from './FactorContextReviewNotice';
import {
  fetchAiIdioModelMetadata,
  saveFactorDataSyncPreferences,
  savePosSizingSyncPreferences,
  updateBetaSource,
  updateMissingFactorPreferences,
  updateRiskContext,
} from '../utils/ApiCall';
import { callBackend } from '../utils/api';
import { getLinkedProviderIds } from '../utils/federatedAuth';
import {
  getAvailableBetaSourceOptions,
  getAvailableBetaSources,
  isBetaSourceAvailable,
  normalizeBetaSource,
} from '../utils/betaSources';
import {
  AXIOMA_FACTOR_DATA_MODEL_OPTIONS,
  AI_IDIO_VOL_DISABLED_REASON,
  BARRA_SHORT_HORIZON_COMING_SOON_TOOLTIP,
  BARRA_FACTOR_DATA_MODEL_OPTIONS,
  coerceIdioVolSourceForFactorModel,
  formatUserFactorModel,
  IDIO_VOL_SOURCE_AI,
  IDIO_VOL_SOURCE_STANDARD,
  normalizeAxiomaFactorDataModel,
  normalizeBarraFactorDataModel,
  normalizeUserFactorModel,
  supportsAiIdioVolForFactorModel,
} from '../utils/factorDataOptions';
import {
  getAppMode,
  isAppProductionMode,
} from '../utils/runtimeEnv';
import {
  readWatchlistM365CallbackResult,
  removeWatchlistM365CallbackResult,
} from '../utils/watchlistM365Callback';
import AppNavigationSidebar from './app_shell/AppNavigationSidebar';
import SecurityResolutionHint from './app_shell/SecurityResolutionHint';
import SetupStepHint from './app_shell/SetupStepHint';
import { persistAppearancePreferences } from '../utils/appearancePrefs';
import { isFdlAdminEnabled, isMsiAdminEnabled } from '../features/admin/shared/config';
import DashboardPageHeader, {
  appBarHeaderSlotSx,
  resolveAppBarActiveView,
} from './dashboard/components/DashboardPageHeader';
import { DASHBOARD_TOOLBAR_ACTIONS_ID } from './dashboard/components/DashboardToolbarPortal';
import {
  APP_SHELL_DIMENSIONS,
  getAppCanvasBackground,
  getAppSidebarWidth,
} from '../styles/appShellStyles';

const FACTOR_MODEL_OPTIONS = [
  { value: 'Barra', tooltip: 'Use Barra as the active risk model.' },
  { value: 'Axioma', tooltip: 'Use Axioma as the active risk model.' },
];
const IDIO_VOL_SOURCE_OPTIONS = [
  { value: IDIO_VOL_SOURCE_STANDARD, label: 'Standard', tooltip: 'Use standard idiosyncratic volatility values.' },
  { value: IDIO_VOL_SOURCE_AI, label: 'AI', tooltip: 'Use AI-enhanced idiosyncratic volatility values when available.' },
];
const APPBAR_UPLOAD_CONTEXT_EVENT = 'alphamizer:appbar-upload-context';
const APPBAR_SYNC_STATE_EVENT = 'alphamizer:appbar-sync-state';
const APPBAR_CONTEXT_LINEAGE_EVENT = 'alphamizer:appbar-context-lineage';
const APPBAR_AUTO_SYNC_UPDATE_EVENT = 'alphamizer:appbar-auto-sync-update';
const APPBAR_UPLOAD_INTERACTION_EVENT = 'alphamizer:appbar-upload-interaction';
const APPBAR_UPLOAD_MODAL_OPEN_EVENT = 'alphamizer:appbar-upload-modal-open';
const RISK_CONTEXT_REFRESH_REQUEST_EVENT = 'alphamizer:risk-context-refresh-request';
const FACTOR_DATA_REFRESH_REQUEST_EVENT = 'alphamizer:factor-data-refresh-request';

// A confirmed factor vendor switch approves its staged re-check. The Dashboard
// claims the request and approves it with its own status tracking; anywhere
// else it is approved here through the same shared approval, and the
// Watchlist's status shows its progress and outcome. The vendor is saved when
// the switch publishes. Returns 'started', 'waiting' (a Watchlist change is
// still being applied) or 'failed'.
async function approveFactorVendorRecheck(transition, sessionScopeToken) {
  if (transition?.status === 'waiting_for_watchlist_update') return 'waiting';
  const successor = transition?.successor;
  if (
    !String(successor?.watchlist_revision_id || '').trim()
    || successor?.status !== 'pending_review'
    || successor?.context?.requires_initial_validation === true
  ) {
    return 'started';
  }
  try {
    announceContextRefreshApproval(
      await applyStagedContextRefresh(successor),
      sessionScopeToken,
    );
  } catch (_error) {
    return 'failed';
  }
  return 'started';
}

// What Save says about a factor vendor switch: 'started', 'waiting' or 'failed'.
// A waiting switch reads the same on the Dashboard and elsewhere, since only
// the open Watchlist page of this session can submit it again.
function factorVendorSwitchSavedNotice(transition, outcome) {
  const target = formatUserFactorModel(transition?.target_factor_model);
  const current = formatUserFactorModel(transition?.factor_model);
  if (outcome === 'waiting') {
    return {
      severity: 'info',
      message: factorVendorSwitchWaitingMessage(target, current),
    };
  }
  if (outcome === 'failed') {
    return {
      severity: 'warning',
      message: `The switch to ${target} didn't start; your factor data source is still ${current}. Choose ${target} again in Settings to try again.`,
    };
  }
  return {
    severity: 'info',
    message: `Checking your Watchlist against ${target}. ${current} remains active until the switch completes.`,
  };
}

const SETTINGS_LIVE_PERFORMANCE_PAGE_SIZE = 20;
const PERFORMANCE_SCOPE_SYSTEM = 'system';
const PERFORMANCE_SCOPE_WATCHLIST = 'watchlist';
const PERFORMANCE_SCOPE_PORTFOLIO = 'portfolio';
const PERFORMANCE_SCOPE_TABS = [
  { value: PERFORMANCE_SCOPE_SYSTEM, label: 'System' },
  { value: PERFORMANCE_SCOPE_WATCHLIST, label: 'Watchlist' },
  { value: PERFORMANCE_SCOPE_PORTFOLIO, label: 'Portfolio' },
];
const PERFORMANCE_SCOPE_DESCRIPTIONS = {
  [PERFORMANCE_SCOPE_SYSTEM]: 'Daily production-era performance rows for the active model, measured across the shared system-wide training and evaluation universe.',
  [PERFORMANCE_SCOPE_WATCHLIST]: 'Recent live-realized performance computed on demand for the securities in your current watchlist.',
  [PERFORMANCE_SCOPE_PORTFOLIO]: 'Recent live-realized performance computed on demand for the securities in your current actual portfolio.',
};
const buildRecentPerformanceTableRows = (metadata) => {
  const liveRows = metadata?.recent_live_realized_rows || [];
  const holdoutRows = metadata?.recent_holdout_test_rows || [];

  if (liveRows.length || holdoutRows.length) {
    const byDate = new Map();
    holdoutRows.forEach((row) => {
      if (row?.prediction_as_of_date) {
        byDate.set(row.prediction_as_of_date, { ...row });
      }
    });
    liveRows.forEach((row) => {
      if (row?.prediction_as_of_date) {
        byDate.set(row.prediction_as_of_date, { ...row });
      }
    });
    return Array.from(byDate.values()).sort((a, b) => (b?.prediction_as_of_date || '').localeCompare(a?.prediction_as_of_date || ''));
  }

  const fallbackRows = metadata?.recent_effective_performance_rows || metadata?.recent_live_performance_rows || [];
  return [...fallbackRows].sort((a, b) => (b?.prediction_as_of_date || '').localeCompare(a?.prediction_as_of_date || ''));
};

const SETTINGS_SECTION_HELP_TEXT = {
  historical_data_deliveries: 'Review prior factor data delivery runs and diagnostics before selecting active models.',
  model_selection: 'Choose the default model provider and vendor-specific factor model variants used for risk calculations.',
  recent_live_performance: 'The System tab shows results across the shared system-wide training and evaluation universe. Watchlist and Portfolio tabs estimate recent live performance for the securities you currently care about.',
};
const SETTINGS_TAB_ITEMS = [
  { value: 'appearance', label: 'Appearance', icon: PaletteOutlinedIcon },
  { value: 'factor_data', label: 'Factor Data', icon: StorageRoundedIcon },
  { value: 'machine_learning', label: 'Machine Learning', icon: AutoAwesomeOutlinedIcon },
  { value: 'risk_settings', label: 'Settings', icon: ShieldOutlinedIcon },
  { value: 'context_lineage', label: 'Data & Sync', icon: AccountTreeOutlinedIcon },
  { value: 'integrations', label: 'Integrations', icon: LinkRoundedIcon },
  { value: 'msi', label: 'Security Resolution', icon: VisibilityOutlinedIcon },
  { value: 'admin', label: 'Admin', icon: AdminPanelSettingsOutlinedIcon },
];
const IDLE_LOGOUT_MINUTES_OPTIONS = [
  { value: 5, label: '5 min' },
  { value: 10, label: '10 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
  { value: null, label: 'Never' },
];
// W8 (2026-08-04) — user-configurable recency window for the Watchlist News
// bright/dull dot next to each row. Default 24 matches the pre-W8 W7 hardcode.
const NEWS_RECENCY_HOURS_OPTIONS = [
  { value: 1, label: '1 hr' },
  { value: 2, label: '2 hr' },
  { value: 6, label: '6 hr' },
  { value: 24, label: '24 hr' },
  { value: 48, label: '48 hr' },
];
// Slugs mirror `_APPROVED_FOCUS_AREAS` in `services/deep_dive_service.py`.
// `all` is mutually exclusive with the specifics — the toggle handler in
// this file enforces the mutex.
const DEEP_DIVE_FOCUS_AREA_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'price_action', label: 'Price action' },
  { value: 'company_news', label: 'Company news' },
  { value: 'earnings_fundamentals', label: 'Earnings & fundamentals' },
  { value: 'macro_sector', label: 'Macro / sector' },
  { value: 'regulatory_legal', label: 'Regulatory / legal' },
  { value: 'risks_catalysts', label: 'Risks / catalysts' },
];
const ADMIN_SETTINGS_TAB_ITEMS = [
  { value: 'customer_organizations', label: 'Customer Organizations' },
  { value: 'team_members', label: 'Team Members' },
  { value: 'factor_data_monitoring', label: 'Factor Data Monitoring' },
  { value: 'system_credentials', label: 'System Credentials' },
  { value: 'market_calendar', label: 'Market Calendar' },
];
const SETTINGS_TAB_DESCRIPTIONS = {
  appearance: 'Theme and density preferences for the workspace.',
  factor_data: 'Model defaults, idio-vol source, and factor-processing behavior.',
  machine_learning: 'Read-only visibility into active AI models, prediction coverage, and factor-model linkage.',
  risk_settings: 'Beta inputs, sync behavior, account-level sizing defaults, and session behavior.',
  context_lineage: 'See which inputs each workspace uses and whether newer parent data is available.',
  integrations: 'Broker, file-based import, and execution-provider connections.',
  msi: 'Watchlist security resolution status, notes, and country choices.',
  admin: 'Manage member registration, admin privileges, and operational monitoring.',
};
const MSI_RESOLUTION_STATUS_METADATA = {
  needs_review: {
    label: 'Needs Review',
    color: 'warning',
  },
  could_not_resolve: {
    label: 'Could Not Resolve',
    color: 'error',
  },
  waiting: {
    label: 'Waiting',
    color: 'info',
  },
  resolved: {
    label: 'Resolved',
    color: 'success',
  },
};
const MSI_RESOLUTION_STATUS_ORDER = [
  'needs_review',
  'could_not_resolve',
  'waiting',
  'resolved',
];
const MSI_APP_READINESS_WAITING_MARKET_REGISTRATION = 'waiting_market_registration';
const MSI_OPTION_PENDING_MARKET_REGISTRATION_STATUSES = new Set([
  'pending',
  'queued',
  'running',
  'replacing',
  'waiting',
]);
const normalizeMsiResolutionText = (value) => (
  value == null ? '' : String(value).trim()
);

const normalizeMsiCount = (value) => {
  const resolved = Number(value);
  if (!Number.isFinite(resolved) || resolved < 0) {
    return 0;
  }
  return Math.floor(resolved);
};

// Phase 4.6: flatten the server-owned Security Resolution read model. The table
// consumes summary/groups/rows/reconciliation_review directly; AppBar never
// reimplements readiness.
const normalizeMsiResolutionCenter = (state, selectedFactorVendor) => {
  const loading = Boolean(state?.loading);
  const error = normalizeMsiResolutionText(state?.error);
  const data = state?.data && typeof state.data === 'object' ? state.data : {};
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const groups = Array.isArray(data?.groups) ? data.groups : [];
  const summary = data?.summary && typeof data.summary === 'object' ? data.summary : {};

  const reconciliationReviewRaw = (
    data?.reconciliation_review && typeof data.reconciliation_review === 'object'
      ? data.reconciliation_review
      : {}
  );
  const reconciliationRows = Array.isArray(reconciliationReviewRaw?.rows)
    ? reconciliationReviewRaw.rows
    : [];
  // The API retains all vendors; reviews and their attention indicators follow
  // the same selected factor source as the rest of the application.
  const { reviews: providerIdentityReviews, groups: providerIdentityReviewGroups } = selectFactorVendorReviews({
    reviews: Array.isArray(data?.provider_identity_reviews) ? data.provider_identity_reviews : [],
    groups: Array.isArray(data?.provider_identity_review_groups) ? data.provider_identity_review_groups : [],
  }, selectedFactorVendor);
  const reconciliationReview = {
    rows: reconciliationRows,
    row_count: normalizeMsiCount(
      reconciliationReviewRaw?.row_count ?? reconciliationRows.length,
    ),
    actionable_count: normalizeMsiCount(reconciliationReviewRaw?.actionable_count),
  };

  const admission = data?.portfolio_analysis_admission
    && typeof data.portfolio_analysis_admission === 'object'
    ? data.portfolio_analysis_admission
    : { factor_data_available: false, readiness_is_final: false };

  return {
    loading,
    loaded: Boolean(state?.data),
    error,
    status: normalizeMsiResolutionText(data?.status).toLowerCase() || 'ok',
    message: normalizeMsiResolutionText(data?.message) || '',
    active_snapshot_version: data?.active_snapshot_version ?? null,
    summary,
    groups,
    rows,
    reconciliation_review: reconciliationReview,
    provider_identity_reviews: providerIdentityReviews,
    provider_identity_review_groups: providerIdentityReviewGroups,
    provider_review_base_revision_id: data?.provider_review_base_revision_id ?? null,
    watchlist_field_contract: data?.watchlist_field_contract ?? null,
    portfolio_analysis_admission: admission,
  };
};

const SETTINGS_SAVE_GROUP_META = {
  factorDataPreferences: {
    label: 'Factor data settings',
    progressLabel: 'Saving factor data settings...',
  },
  riskContext: {
    label: 'Risk context',
    progressLabel: 'Saving risk context...',
  },
  betaSource: {
    label: 'Beta source',
    progressLabel: 'Saving beta source...',
  },
  syncPreferences: {
    label: 'Sync preferences',
    progressLabel: 'Applying sync preferences...',
  },
};
const SETTINGS_DISPLAY_TITLE_SX = {
  fontWeight: 800,
  letterSpacing: '-0.02em',
  lineHeight: 1.12,
};
const SETTINGS_SECTION_TITLE_SX = {
  fontWeight: 750,
  letterSpacing: 0,
  lineHeight: 1.25,
};
const buildConfirmedSettingsState = ({ userData, syncControlState }) => ({
  axiomaModel: normalizeAxiomaFactorDataModel(userData?.axioma_model),
  barraModel: normalizeBarraFactorDataModel(userData?.barra_model),
  factorModel: formatUserFactorModel(userData?.factor_model),
  idioVolSource: coerceIdioVolSourceForFactorModel(
    userData?.factor_model,
    userData?.idio_vol_source,
  ),
  betaSource: normalizeBetaSource(userData?.beta_source),
  autoRefreshEnabled: Boolean(syncControlState?.autoRefreshEnabled),
  posSizingAutoSyncEnabled: Boolean(syncControlState?.posSizingAutoSyncEnabled),
  factorDataAutoSyncEnabled: Boolean(syncControlState?.factorDataAutoSyncEnabled),
});
const SettingsSection = React.memo(function SettingsSection({
  title,
  description,
  infoKey,
  action = null,
  children,
  sectionRef = null,
  paperSx,
  onInfoClick,
}) {
  return (
    <Box
      ref={sectionRef}
      sx={{
        scrollMarginTop: 20,
      }}
    >
      <Paper variant="outlined" sx={paperSx}>
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.3, md: 1.55 },
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: (theme) => alpha(
              theme.palette.action.hover,
              theme.palette.mode === 'dark' ? 0.28 : 0.48,
            ),
          }}
        >
          <Box sx={{ display: 'grid', gap: 0.35, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45 }}>
              <Typography sx={{ ...SETTINGS_SECTION_TITLE_SX, fontSize: '0.86rem', color: 'text.primary' }}>
                {title}
              </Typography>
              {infoKey && onInfoClick && (
                <IconButton
                  size="small"
                  aria-label={`${title} info`}
                  onClick={(event) => onInfoClick(event, infoKey)}
                  sx={{ width: 22, height: 22, color: 'text.secondary' }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              )}
            </Box>
            {description && (
              <Typography sx={{ fontSize: '0.73rem', color: 'text.secondary', lineHeight: 1.55 }}>
                {description}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
        {children}
      </Paper>
    </Box>
  );
});

const SettingsRow = React.memo(function SettingsRow({ label, description, children }) {
  return (
    <Box
      sx={{
        px: { xs: 1.4, md: 1.8 },
        py: 1.35,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 1, md: 2.5 },
        minHeight: { md: 74 },
        transition: 'background-color 140ms ease',
        '&:hover': {
          backgroundColor: (theme) => alpha(
            theme.palette.action.hover,
            theme.palette.mode === 'dark' ? 0.2 : 0.34,
          ),
        },
      }}
    >
      <Box sx={{ flex: { md: '0 0 300px' }, maxWidth: { md: 330 }, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.81rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.45 }}>
          {label}
        </Typography>
        {description && (
          <Typography sx={{ mt: 0.3, fontSize: '0.71rem', color: 'text.secondary', lineHeight: 1.55 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
});

const SettingsChoiceButton = React.memo(function SettingsChoiceButton({
  label,
  description,
  onClick,
  disabled = false,
  sx,
}) {
  return (
    <Button
      fullWidth
      disableElevation
      disabled={disabled}
      onClick={onClick}
      sx={sx}
    >
      <Box sx={{ display: 'grid', gap: 0.3, textAlign: 'left' }}>
        <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: 'inherit' }}>
          {label}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: '0.67rem', lineHeight: 1.45, color: 'text.secondary' }}>
            {description}
          </Typography>
        )}
      </Box>
    </Button>
  );
});

const formatAiMetricValue = (value, digits = 3) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '—';
  }
  return numeric.toFixed(digits);
};

const formatAiMetricDeltaValue = (value, digits = 3) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '—';
  }
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${numeric.toFixed(digits)}`;
};

const formatAiMetricImprovementPct = (improvementValue, baselineValue, digits = 1) => {
  const improvement = Number(improvementValue);
  const baseline = Number(baselineValue);
  if (!Number.isFinite(improvement) || !Number.isFinite(baseline) || baseline === 0) {
    return '—';
  }
  const pct = (improvement / baseline) * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(digits)}%`;
};

const getAiMetricDeltaTextSx = (value) => ({
  color: (theme) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric === 0) {
      return theme.palette.text.primary;
    }
    return numeric > 0 ? theme.palette.success.main : theme.palette.error.main;
  },
});

const formatSettingsDateTime = (value, { includeTime = true } = {}) => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    ...(includeTime ? { timeStyle: 'short' } : {}),
  }).format(parsed);
};

const AppBarComponent = ({
  navigationCollapsed = false,
  onNavigationCollapsedChange = null,
  user,
  userData,
  onUserDataChange,
  isAdmin = false,
  currentEnv = 'dev',
  compactMode,
  toggleCompactMode,
  msiResolutionCenter,
  onEnsureMsiResolutionCenterLoaded,
  onRefreshMsiResolutionCenter,
  securityOperationStatus = null,
  watchlistPublicationState = null,
  themeId,
  setThemeId,
  colorOverrides,
  setColorOverride,
  resetColorOverride,
  resetAllColorOverrides,
  idleLogoutMinutes,
  setIdleLogoutMinutes,
  newsRecencyHours = 24,
  setNewsRecencyHours = null,
  deepDiveFocusAreas = null,
  setDeepDiveFocusAreas = null,
}) => {
  const defaultUploadContext = useMemo(() => ({
    selectedFactorModel: formatUserFactorModel(userData?.factor_model),
    serverNormalVol: null,
    serverMaxPosSize: null,
    aum: null,
    defaultTargetVolPct: null,
    defaultTargetVolDollar: null,
    defaultScaler: null,
    watchlistIsReady: null,
    factorDataFreshnessSummary: null,
    factorDataLoadDisabled: false,
    factorDataLoadBlockedReason: null,
    factorDataLoadInProgress: false,
    factorContextUpdateProgress: null,
    factorDataDetailsOpen: false,
    onFactorDataDetailsOpenChange: null,
  }), [userData?.factor_model]);
  const defaultSyncState = useMemo(() => ({
    autoRefreshEnabled: true,
    posSizingAutoSyncEnabled: false,
    factorDataAutoSyncEnabled: false,
    posSizingAutoSyncToggleBusy: false,
    autoRefreshPending: false,
    pendingAutoRefreshCount: 0,
    pendingPosSizingAutoSync: false,
    riskAutoSyncTooltipText: '',
    posSizingAutoSyncTooltip: '',
  }), []);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [linkedProviderIds, setLinkedProviderIds] = useState(() => getLinkedProviderIds(user));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [m365CallbackResult, setM365CallbackResult] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [uploadModalMode, setUploadModalMode] = useState('imports');
  const [uploadReviewRun, setUploadReviewRun] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSnackbar, setSettingsSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [settingsTab, setSettingsTab] = useState('appearance');
  const [msiResolutionFocusTarget, setMsiResolutionFocusTarget] = useState(null);
  const [adminSettingsTab, setAdminSettingsTab] = useState('team_members');
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminInvitations, setAdminInvitations] = useState([]);
  const [customerOrganizations, setCustomerOrganizations] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersError, setAdminUsersError] = useState('');
  const [adminUsersOpenGeneration, setAdminUsersOpenGeneration] = useState(0);
  const [adminUsersAttemptedGeneration, setAdminUsersAttemptedGeneration] = useState(null);
  const [executionSourceRefreshToken, setExecutionSourceRefreshToken] = useState(0);
  const [settingsAxiomaModel, setSettingsAxiomaModel] = useState(
    normalizeAxiomaFactorDataModel(userData?.axioma_model)
  );
  const [settingsBarraModel, setSettingsBarraModel] = useState(
    normalizeBarraFactorDataModel(userData?.barra_model)
  );
  const [settingsFactorModel, setSettingsFactorModel] = useState(
    formatUserFactorModel(userData?.factor_model)
  );
  const [settingsIdioVolSource, setSettingsIdioVolSource] = useState(
    coerceIdioVolSourceForFactorModel(userData?.factor_model, userData?.idio_vol_source)
  );
  const [settingsBetaSource, setSettingsBetaSource] = useState(
    normalizeBetaSource(userData?.beta_source)
  );
  const [settingsAutoRefreshEnabled, setSettingsAutoRefreshEnabled] = useState(true);
  const [settingsPosSizingAutoSyncEnabled, setSettingsPosSizingAutoSyncEnabled] = useState(false);
  const [settingsFactorDataAutoSyncEnabled, setSettingsFactorDataAutoSyncEnabled] = useState(false);
  const [settingsInfoAnchorEl, setSettingsInfoAnchorEl] = useState(null);
  const [settingsInfoContent, setSettingsInfoContent] = useState('');
  // W6 (2026-08-06): a Settings-drawer entry for the BYOK Deep Dive credentials
  // dialog. Owns only its open state — the dialog itself persists via
  // `services/llmCredentials.js`'s localStorage entry and never rides the
  // `appearance_preferences` autosave chain (that would round-trip the key
  // through the FastAPI backend, defeating the whole BYOK posture).
  const [llmSettingsOpen, setLlmSettingsOpen] = useState(false);
  const [settingsAiPerformanceLimit, setSettingsAiPerformanceLimit] = useState(SETTINGS_LIVE_PERFORMANCE_PAGE_SIZE);
  const [settingsAiPerformanceScope, setSettingsAiPerformanceScope] = useState(PERFORMANCE_SCOPE_SYSTEM);
  const [uploadContext, setUploadContext] = useState(defaultUploadContext);
  const [syncControlState, setSyncControlState] = useState(defaultSyncState);
  const [contextLineage, setContextLineage] = useState(null);
  // Save asks before switching the factor vendor: the question and the
  // resolver of its answer (true switch, false keep, null back to Settings).
  const [vendorSwitchQuestion, setVendorSwitchQuestion] = useState(null);
  // A switch this Settings visit started, shown until Settings closes.
  const [startedVendorSwitch, setStartedVendorSwitch] = useState(null);
  // The published Watchlist's context status, kept current by the Dashboard.
  // It reports a Settings vendor switch that has not published.
  const publishedContextStatus = contextLineage?.userUid === user?.uid
    ? contextLineage?.watchlistActual?.contextStatus ?? null
    : null;
  const startedVendorSwitchFailed = Boolean(startedVendorSwitch?.revision_id
    && publishedContextStatus?.factor_vendor_switch?.revision_id === startedVendorSwitch.revision_id
    && publishedContextStatus.factor_vendor_switch.state === 'failed');
  const settingsVendorSwitch = (startedVendorSwitchFailed ? null : startedVendorSwitch)
    ?? publishedContextStatus?.factor_vendor_switch
    ?? null;
  const settingsVendorSwitchRunning = isFactorVendorSwitchRunning(settingsVendorSwitch);
  // A cancelled switch is noted until the member acts in these settings or the
  // Watchlist is current again.
  const [acknowledgedVendorSwitchRevision, setAcknowledgedVendorSwitchRevision] = useState('');
  // The operation already reports a failure once. Settings returns to the
  // saved source; a historical failure must not become a permanent warning.
  const settingsVendorSwitchNotice = settingsVendorSwitch?.state === 'failed' ? null : (
    settingsVendorSwitch?.state === 'cancelled'
    && (
      settingsVendorSwitch.revision_id === acknowledgedVendorSwitchRevision
      || publishedContextStatus?.status === 'current'
    )
  ) ? null : settingsVendorSwitch;
  useEffect(() => {
    if (!startedVendorSwitchFailed) return;
    setStartedVendorSwitch(null);
    setSettingsFactorModel(formatUserFactorModel(userData?.factor_model));
  }, [startedVendorSwitchFailed, userData?.factor_model]);
  const acknowledgeVendorSwitchNotice = useCallback(() => {
    if (settingsVendorSwitch?.state === 'cancelled') {
      setAcknowledgedVendorSwitchRevision(String(settingsVendorSwitch.revision_id || ''));
    }
  }, [settingsVendorSwitch]);
  const [settingsConfirmedState, setSettingsConfirmedState] = useState(() => (
    buildConfirmedSettingsState({ userData, syncControlState: defaultSyncState })
  ));
  const [settingsSaveStepLabel, setSettingsSaveStepLabel] = useState('');
  const [settingsSaveErrors, setSettingsSaveErrors] = useState([]);
  const settingsContentRef = useRef(null);
  const adminUsersOpenGenerationRef = useRef(0);
  const adminUsersActiveGenerationRef = useRef(null);
  const adminUsersAttemptedGenerationRef = useRef(null);
  const adminUsersRequestInFlightRef = useRef(null);
  const adminUsersRequestSequenceRef = useRef(0);
  const useCompactSettingsNavigation = useMediaQuery('(max-width:899.95px)');
  const location = useLocation();
  const navigate = useNavigate();
  const activeAppBarView = resolveAppBarActiveView(location.pathname);
  const {
    aiIdioVolStatus,
    setupGuideState,
    setupGuideDismissed,
    dismissSetupGuide,
    sectorPalette,
    sectorPaletteOverride,
    setSectorColor,
    resetSectorColor,
    resetSectorPalette,
  } = useAppContext();
  const { sessionScopeToken: securityOperationSessionScopeToken } = useSecurityOperationContext();
  const appBarFactorUpdateProgress = uploadContext.factorContextUpdateProgress?.userUid === user?.uid
    && uploadContext.factorContextUpdateProgress?.sessionScopeToken === securityOperationSessionScopeToken
    ? uploadContext.factorContextUpdateProgress : null;
  const settingsAvailableBetaSources = useMemo(
    () => getAvailableBetaSources({ available_beta_sources: userData?.available_beta_sources }),
    [userData?.available_beta_sources],
  );
  const settingsBetaSourceOptions = useMemo(
    () => getAvailableBetaSourceOptions({ available_beta_sources: userData?.available_beta_sources }),
    [userData?.available_beta_sources],
  );
  const settingsAiIdioSupported = useMemo(
    () => supportsAiIdioVolForFactorModel(settingsFactorModel),
    [settingsFactorModel],
  );
  const settingsActiveProviderKey = useMemo(
    () => normalizeUserFactorModel(settingsFactorModel),
    [settingsFactorModel],
  );
  const settingsActiveFactorDataModel = useMemo(() => (
    settingsActiveProviderKey === 'barra'
      ? normalizeBarraFactorDataModel(settingsBarraModel)
      : normalizeAxiomaFactorDataModel(settingsAxiomaModel)
  ), [settingsActiveProviderKey, settingsAxiomaModel, settingsBarraModel]);
  const settingsActiveFactorDataModelDescription = useMemo(() => {
    const options = settingsActiveProviderKey === 'barra'
      ? BARRA_FACTOR_DATA_MODEL_OPTIONS
      : AXIOMA_FACTOR_DATA_MODEL_OPTIONS;
    return options.find((option) => option.value === settingsActiveFactorDataModel)?.description || '';
  }, [settingsActiveFactorDataModel, settingsActiveProviderKey]);
  const settingsLatestAiStatus = useMemo(() => {
    if (!aiIdioVolStatus || normalizeUserFactorModel(aiIdioVolStatus?.factorModel) !== 'barra') {
      return null;
    }
    if (
      normalizeBarraFactorDataModel(aiIdioVolStatus?.factorDataModel) !== normalizeBarraFactorDataModel(settingsBarraModel)
    ) {
      return null;
    }
    return aiIdioVolStatus;
  }, [aiIdioVolStatus, settingsBarraModel]);
  const settingsAiFactorSnapshotDate = settingsLatestAiStatus?.factorSnapshotDate ?? null;
  const aiIdioModelMetadataQuery = useQuery({
    queryKey: [
      'settings-ai-idio-model-metadata',
      settingsFactorModel,
      settingsBarraModel,
      settingsAiFactorSnapshotDate,
      settingsAiPerformanceScope,
      settingsAiPerformanceLimit,
    ],
    queryFn: async () => {
      const response = await fetchAiIdioModelMetadata({
        factorModel: settingsFactorModel,
        factorDataModel: settingsBarraModel,
        factorSnapshotDate: settingsAiFactorSnapshotDate,
        livePerformanceLimit: settingsAiPerformanceLimit,
        performanceScope: settingsAiPerformanceScope,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Unable to load AI model metadata.');
      }
      return response?.metadata ?? null;
    },
    enabled: settingsOpen && settingsAiIdioSupported,
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const settingsAiUnsupportedMetadata = useMemo(() => ({
    ai_supported: false,
    provider: settingsActiveProviderKey,
    factor_data_model: settingsActiveFactorDataModel,
    model_name: null,
    status: 'msi_v2_localid_required',
  }), [settingsActiveFactorDataModel, settingsActiveProviderKey]);
  const settingsAiModelMetadata = settingsAiIdioSupported
    ? (aiIdioModelMetadataQuery.data ?? null)
    : settingsAiUnsupportedMetadata;
  const settingsAiModelMetadataLoading = settingsAiIdioSupported
    && aiIdioModelMetadataQuery.isFetching
    && !aiIdioModelMetadataQuery.data;
  const settingsRecentPerformanceTableRows = useMemo(() => (
    buildRecentPerformanceTableRows(settingsAiModelMetadata)
  ), [settingsAiModelMetadata]);
  const externalConfirmedSettingsState = useMemo(() => (
    buildConfirmedSettingsState({
      userData,
      syncControlState: {
        autoRefreshEnabled: syncControlState.autoRefreshEnabled,
        posSizingAutoSyncEnabled: syncControlState.posSizingAutoSyncEnabled,
        factorDataAutoSyncEnabled: syncControlState.factorDataAutoSyncEnabled,
      },
    })
  ), [
    syncControlState.autoRefreshEnabled,
    syncControlState.posSizingAutoSyncEnabled,
    syncControlState.factorDataAutoSyncEnabled,
    userData?.axioma_model,
    userData?.barra_model,
    userData?.beta_source,
    userData?.factor_model,
    userData?.idio_vol_source,
    userData?.missing_factor_policy,
  ]);
  const clearSettingsApplyErrors = useCallback(() => {
    setSettingsSaveErrors((current) => (current.length > 0 ? [] : current));
  }, []);
  const applySettingsDraftFromConfirmedState = useCallback((confirmedState) => {
    if (!confirmedState) {
      return;
    }
    setSettingsAxiomaModel(confirmedState.axiomaModel);
    setSettingsBarraModel(confirmedState.barraModel);
    setSettingsFactorModel(confirmedState.factorModel);
    setSettingsIdioVolSource(
      coerceIdioVolSourceForFactorModel(confirmedState.factorModel, confirmedState.idioVolSource),
    );
    setSettingsBetaSource(normalizeBetaSource(confirmedState.betaSource));
    setSettingsAutoRefreshEnabled(Boolean(confirmedState.autoRefreshEnabled));
    setSettingsPosSizingAutoSyncEnabled(Boolean(confirmedState.posSizingAutoSyncEnabled));
    setSettingsFactorDataAutoSyncEnabled(Boolean(confirmedState.factorDataAutoSyncEnabled));
  }, []);
  const revertSettingsGroupToConfirmed = useCallback((groupKey, confirmedState) => {
    if (!confirmedState) {
      return;
    }
    if (groupKey === 'factorDataPreferences') {
      setSettingsAxiomaModel(confirmedState.axiomaModel);
      setSettingsBarraModel(confirmedState.barraModel);
      return;
    }
    if (groupKey === 'riskContext') {
      setSettingsFactorModel(confirmedState.factorModel);
      setSettingsIdioVolSource(
        coerceIdioVolSourceForFactorModel(confirmedState.factorModel, confirmedState.idioVolSource),
      );
      return;
    }
    if (groupKey === 'betaSource') {
      setSettingsBetaSource(normalizeBetaSource(confirmedState.betaSource));
      return;
    }
    if (groupKey === 'syncPreferences') {
      setSettingsAutoRefreshEnabled(Boolean(confirmedState.autoRefreshEnabled));
      setSettingsPosSizingAutoSyncEnabled(Boolean(confirmedState.posSizingAutoSyncEnabled));
      setSettingsFactorDataAutoSyncEnabled(Boolean(confirmedState.factorDataAutoSyncEnabled));
    }
  }, []);

  useEffect(() => {
    setSettingsIdioVolSource((current) => coerceIdioVolSourceForFactorModel(settingsFactorModel, current));
  }, [settingsFactorModel]);

  useEffect(() => {
    setUploadContext(defaultUploadContext);
  }, [defaultUploadContext]);

  useEffect(() => {
    const handleUploadContext = (event) => {
      setUploadContext((current) => ({
        ...current,
        ...(event?.detail || {}),
      }));
    };
    const handleSyncState = (event) => {
      setSyncControlState((current) => ({
        ...current,
        ...(event?.detail || {}),
      }));
    };
    const handleContextLineage = (event) => {
      setContextLineage(event?.detail || null);
    };
    window.addEventListener(APPBAR_UPLOAD_CONTEXT_EVENT, handleUploadContext);
    window.addEventListener(APPBAR_SYNC_STATE_EVENT, handleSyncState);
    window.addEventListener(APPBAR_CONTEXT_LINEAGE_EVENT, handleContextLineage);
    return () => {
      window.removeEventListener(APPBAR_UPLOAD_CONTEXT_EVENT, handleUploadContext);
      window.removeEventListener(APPBAR_SYNC_STATE_EVENT, handleSyncState);
      window.removeEventListener(APPBAR_CONTEXT_LINEAGE_EVENT, handleContextLineage);
    };
  }, []);

  useEffect(() => {
    setSettingsConfirmedState(externalConfirmedSettingsState);
    if (!settingsOpen) {
      applySettingsDraftFromConfirmedState(externalConfirmedSettingsState);
    }
  }, [applySettingsDraftFromConfirmedState, externalConfirmedSettingsState, settingsOpen]);

  useEffect(() => {
    if (!isBetaSourceAvailable(settingsAvailableBetaSources, settingsBetaSource)) {
      setSettingsBetaSource('yahoo');
    }
  }, [settingsAvailableBetaSources, settingsBetaSource]);

  useEffect(() => {
    setSettingsAiPerformanceLimit(SETTINGS_LIVE_PERFORMANCE_PAGE_SIZE);
  }, [
    settingsFactorModel,
    settingsBarraModel,
    settingsAxiomaModel,
    settingsLatestAiStatus?.factorSnapshotDate,
    settingsOpen,
  ]);

  useEffect(() => {
    if (!settingsOpen) {
      setSettingsAiPerformanceScope(PERFORMANCE_SCOPE_SYSTEM);
    }
  }, [settingsOpen]);

  useEffect(() => {
    if (!settingsOpen) {
      return;
    }
    const container = settingsContentRef.current;
    if (container && typeof container.scrollTo === 'function') {
      container.scrollTo({ top: 0 });
    }
  }, [settingsOpen, settingsTab]);

  useEffect(() => {
    if (!settingsOpen || settingsTab !== 'admin') {
      setAdminSettingsTab('team_members');
    }
  }, [settingsOpen, settingsTab]);

  const beginAdminUsersOpenGeneration = useCallback(() => {
    const nextGeneration = adminUsersOpenGenerationRef.current + 1;
    adminUsersOpenGenerationRef.current = nextGeneration;
    adminUsersActiveGenerationRef.current = nextGeneration;
    setAdminUsersOpenGeneration(nextGeneration);
    setAdminUsers([]);
    setAdminInvitations([]);
    setCustomerOrganizations([]);
    setAdminUsersError('');
  }, []);

  const fetchAdminUsers = useCallback(async ({ force = false } = {}) => {
    const generation = adminUsersActiveGenerationRef.current;
    if (generation === null || adminUsersRequestInFlightRef.current) {
      return false;
    }
    if (!force && adminUsersAttemptedGenerationRef.current === generation) {
      return false;
    }

    const requestId = adminUsersRequestSequenceRef.current + 1;
    adminUsersRequestSequenceRef.current = requestId;
    adminUsersAttemptedGenerationRef.current = generation;
    setAdminUsersAttemptedGeneration(generation);
    adminUsersRequestInFlightRef.current = { generation, requestId };
    setAdminUsersLoading(true);
    setAdminUsersError('');
    try {
      const [resp, organizationResponse] = await Promise.all([
        callBackend('admin/users', 'GET'),
        callBackend('admin/customer-organizations', 'GET'),
      ]);
      if (adminUsersActiveGenerationRef.current === generation) {
        setAdminUsers(Array.isArray(resp?.users) ? resp.users : []);
        setAdminInvitations(Array.isArray(resp?.invitations) ? resp.invitations : []);
        setCustomerOrganizations(
          Array.isArray(organizationResponse?.organizations)
            ? organizationResponse.organizations
            : [],
        );
      }
    } catch (err) {
      if (adminUsersActiveGenerationRef.current === generation) {
        setAdminUsers([]);
        setAdminInvitations([]);
        setCustomerOrganizations([]);
        setAdminUsersError(err?.message || 'Failed to load users.');
      }
    } finally {
      const activeRequest = adminUsersRequestInFlightRef.current;
      if (activeRequest?.requestId === requestId) {
        adminUsersRequestInFlightRef.current = null;
        setAdminUsersLoading(false);
      }
    }
    return true;
  }, []);

  useEffect(() => {
    if (
      settingsOpen
      && isAdmin
      && settingsTab === 'admin'
      && ['customer_organizations', 'team_members'].includes(adminSettingsTab)
      && adminUsersAttemptedGeneration !== adminUsersOpenGeneration
      && !adminUsersLoading
    ) {
      void fetchAdminUsers();
    }
  }, [
    adminSettingsTab,
    adminUsersAttemptedGeneration,
    adminUsersLoading,
    adminUsersOpenGeneration,
    fetchAdminUsers,
    isAdmin,
    settingsOpen,
    settingsTab,
  ]);

  useEffect(() => {
    if (!settingsOpen || settingsTab !== 'msi') {
      return;
    }
    onEnsureMsiResolutionCenterLoaded?.();
  }, [onEnsureMsiResolutionCenterLoaded, settingsOpen, settingsTab]);

  const handleOpenSettingsSectionInfo = (event, sectionKey) => {
    const nextText = SETTINGS_SECTION_HELP_TEXT[sectionKey] || '';
    if (!nextText) return;
    event.preventDefault();
    event.stopPropagation();
    setSettingsInfoAnchorEl(event.currentTarget);
    setSettingsInfoContent(nextText);
  };

  const handleCloseSettingsSectionInfo = () => {
    setSettingsInfoAnchorEl(null);
    setSettingsInfoContent('');
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await performLogout(user);
  };

  const handleResetPassword = () => {
    handleProfileMenuClose();
    setShowResetPasswordModal(true);
  };

  const handleResetPasswordModalClose = () => {
    setShowResetPasswordModal(false);
  }

  useEffect(() => {
    setLinkedProviderIds(getLinkedProviderIds(user));
  }, [user]);

  const handleIntegrationsStatusChange = useCallback(() => {
    setExecutionSourceRefreshToken((current) => current + 1);
  }, []);

  const handleExecutionProviderSelectionChange = useCallback((providerId) => {
    onUserDataChange?.({
      selected_execution_provider_id: providerId || null,
    });
  }, [onUserDataChange]);

  const { pending: transferredImport } = useImportFileTransfer();
  useEffect(() => {
    if (!transferredImport) return;
    // A file or sheet handed to the portfolio import is an upload, as the upload button's is.
    if (transferredImport.destination === 'portfolio') {
      window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_INTERACTION_EVENT));
    }
    setUploadModalOpen(transferredImport.destination === 'portfolio');
    setUploadModalMode('imports');
    setUploadReviewRun(null);
  }, [transferredImport]);

  const handleOpenUploadModal = useCallback(() => {
    window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_INTERACTION_EVENT));
    setUploadModalMode('imports');
    setUploadReviewRun(null);
    setUploadModalOpen(true);
  }, []);

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
    setUploadModalMode('imports');
    setUploadReviewRun(null);
  };

  useEffect(() => {
    const handleOpenUploadModalEvent = (event) => {
      if (event?.detail?.mode === 'review') {
        setUploadModalMode('review');
        setUploadReviewRun(event.detail.importRun || null);
        setUploadModalOpen(true);
        return;
      }
      handleOpenUploadModal();
    };
    window.addEventListener(APPBAR_UPLOAD_MODAL_OPEN_EVENT, handleOpenUploadModalEvent);
    return () => {
      window.removeEventListener(APPBAR_UPLOAD_MODAL_OPEN_EVENT, handleOpenUploadModalEvent);
    };
  }, [handleOpenUploadModal]);

  const dispatchAutoSyncUpdate = useCallback((detail = {}) => {
    window.dispatchEvent(new CustomEvent(APPBAR_AUTO_SYNC_UPDATE_EVENT, { detail }));
  }, []);

  const handleToggleRiskAutoSync = useCallback(() => {
    clearSettingsApplyErrors();
    setSettingsAutoRefreshEnabled((current) => !current);
  }, [clearSettingsApplyErrors]);

  const handleTogglePosSizingAutoSync = useCallback(() => {
    if (syncControlState.posSizingAutoSyncToggleBusy) {
      return;
    }
    clearSettingsApplyErrors();
    setSettingsPosSizingAutoSyncEnabled((current) => !current);
  }, [clearSettingsApplyErrors, syncControlState.posSizingAutoSyncToggleBusy]);

  const handleToggleFactorDataAutoSync = useCallback(() => {
    clearSettingsApplyErrors();
    setSettingsFactorDataAutoSyncEnabled((current) => !current);
  }, [clearSettingsApplyErrors]);

  const settingsDirtyState = useMemo(() => {
    const nextFactorModel = formatUserFactorModel(settingsFactorModel);
    const nextIdioVolSource = coerceIdioVolSourceForFactorModel(
      nextFactorModel,
      settingsIdioVolSource,
    );
    return {
      factorDataPreferences: (
        settingsAxiomaModel !== settingsConfirmedState.axiomaModel
        || settingsBarraModel !== settingsConfirmedState.barraModel
      ),
      riskContext: (
        nextFactorModel !== settingsConfirmedState.factorModel
        || nextIdioVolSource !== settingsConfirmedState.idioVolSource
      ),
      betaSource: normalizeBetaSource(settingsBetaSource) !== settingsConfirmedState.betaSource,
      syncPreferences: (
        settingsAutoRefreshEnabled !== Boolean(settingsConfirmedState.autoRefreshEnabled)
        || settingsPosSizingAutoSyncEnabled !== Boolean(settingsConfirmedState.posSizingAutoSyncEnabled)
        || settingsFactorDataAutoSyncEnabled !== Boolean(settingsConfirmedState.factorDataAutoSyncEnabled)
      ),
    };
  }, [
    settingsAutoRefreshEnabled,
    settingsAxiomaModel,
    settingsBarraModel,
    settingsBetaSource,
    settingsConfirmedState,
    settingsFactorModel,
    settingsIdioVolSource,
    settingsPosSizingAutoSyncEnabled,
    settingsFactorDataAutoSyncEnabled,
  ]);
  const settingsPendingChangeCount = useMemo(
    () => Object.values(settingsDirtyState).filter(Boolean).length,
    [settingsDirtyState],
  );
  const settingsHasPendingChanges = settingsPendingChangeCount > 0;
  const settingsPendingSummary = settingsPendingChangeCount === 1
    ? '1 pending change'
    : `${settingsPendingChangeCount} pending changes`;
  const closeSettingsDialog = useCallback(() => {
    setSettingsInfoAnchorEl(null);
    setSettingsInfoContent('');
    setSettingsSaveErrors([]);
    setSettingsSaveStepLabel('');
    setM365CallbackResult(null);
    setStartedVendorSwitch(null);
    adminUsersActiveGenerationRef.current = null;
    setSettingsOpen(false);
  }, []);

  const handleSaveSettings = useCallback(async () => {
    if (settingsSaving) {
      return false;
    }
    if (!settingsHasPendingChanges) {
      closeSettingsDialog();
      return true;
    }

    // Another vendor re-checks the published Watchlist, which switches when
    // the check completes, so Save asks first. Keep saves everything else.
    const savedVendor = normalizeUserFactorModel(settingsConfirmedState.factorModel);
    const selectedVendor = normalizeUserFactorModel(settingsFactorModel);
    let keepVendor = false;
    if (
      settingsDirtyState.riskContext
      && selectedVendor !== savedVendor
      && publishedContextStatus?.status !== 'no_published_watchlist'
      && userData?.watchlist_version_count !== 0
    ) {
      const answer = await new Promise((resolve) => {
        setVendorSwitchQuestion({
          target: selectedVendor,
          current: savedVendor,
          resolve,
        });
      });
      setVendorSwitchQuestion(null);
      if (answer === null) {
        return false;
      }
      keepVendor = answer === false;
      if (keepVendor) {
        setSettingsFactorModel(settingsConfirmedState.factorModel);
      }
    }

    setSettingsInfoAnchorEl(null);
    setSettingsInfoContent('');
    setSettingsSaveErrors([]);
    setSettingsSaveStepLabel('');
    setSettingsSaving(true);

    const dirtyState = { ...settingsDirtyState };
    const startingConfirmedState = { ...settingsConfirmedState };
    const nextConfirmedState = { ...settingsConfirmedState };
    const userDataUpdates = {};
    const nextSaveErrors = [];
    let didSaveRiskContext = false;
    let didSaveFactorDataModelSelection = false;
    let didSaveBetaSource = false;
    let factorVendorTransition = null;

    try {
      const nextFactorModel = keepVendor ? savedVendor : selectedVendor;
      const nextIdioVolSource = coerceIdioVolSourceForFactorModel(
        nextFactorModel,
        settingsIdioVolSource,
      );
      if (keepVendor && nextIdioVolSource === startingConfirmedState.idioVolSource) {
        dirtyState.riskContext = false;
      }
      const nextBetaSource = normalizeBetaSource(settingsBetaSource);
      const factorDataModelSelectionDirty = (
        settingsAxiomaModel !== startingConfirmedState.axiomaModel
        || settingsBarraModel !== startingConfirmedState.barraModel
      );

      if (dirtyState.factorDataPreferences) {
        setSettingsSaveStepLabel(SETTINGS_SAVE_GROUP_META.factorDataPreferences.progressLabel);
        try {
          const factorPrefsPayload = { policy: 'ignore' };
          if (factorDataModelSelectionDirty) {
            factorPrefsPayload.axiomaModel = settingsAxiomaModel;
            factorPrefsPayload.barraModel = settingsBarraModel;
          }
          const factorPrefsResponse = await updateMissingFactorPreferences(factorPrefsPayload);
          if (!factorPrefsResponse?.success) {
            throw new Error(factorPrefsResponse?.message || 'Failed to save factor-data settings.');
          }
          nextConfirmedState.axiomaModel = factorDataModelSelectionDirty
            ? normalizeAxiomaFactorDataModel(factorPrefsResponse.axioma_model)
            : settingsConfirmedState.axiomaModel;
          nextConfirmedState.barraModel = factorDataModelSelectionDirty
            ? normalizeBarraFactorDataModel(factorPrefsResponse.barra_model)
            : settingsConfirmedState.barraModel;
          userDataUpdates.missing_factor_policy = 'ignore';
          if (factorDataModelSelectionDirty) {
            userDataUpdates.axioma_model = factorPrefsResponse.axioma_model;
            userDataUpdates.barra_model = factorPrefsResponse.barra_model;
          }
          didSaveFactorDataModelSelection = factorDataModelSelectionDirty;
        } catch (error) {
          console.error('Error saving factor-data settings:', error);
          nextSaveErrors.push({
            groupKey: 'factorDataPreferences',
            label: SETTINGS_SAVE_GROUP_META.factorDataPreferences.label,
            message: error?.message || 'Failed to save factor-data settings.',
          });
        }
      }

      if (dirtyState.riskContext) {
        setSettingsSaveStepLabel(SETTINGS_SAVE_GROUP_META.riskContext.progressLabel);
        try {
          const riskContextResponse = await updateRiskContext({
            factorModel: nextFactorModel,
            idioVolSource: nextIdioVolSource,
          });
          if (!riskContextResponse?.success) {
            throw new Error(riskContextResponse?.message || 'Failed to save risk context settings.');
          }
          const confirmedFactorModel = formatUserFactorModel(
            riskContextResponse?.factor_model ?? nextFactorModel,
          );
          const confirmedIdioVolSource = coerceIdioVolSourceForFactorModel(
            confirmedFactorModel,
            riskContextResponse?.idio_vol_source ?? nextIdioVolSource,
          );
          nextConfirmedState.factorModel = confirmedFactorModel;
          nextConfirmedState.idioVolSource = confirmedIdioVolSource;
          userDataUpdates.factor_model = normalizeUserFactorModel(
            riskContextResponse?.factor_model ?? nextFactorModel,
          );
          userDataUpdates.idio_vol_source = confirmedIdioVolSource;
          factorVendorTransition = riskContextResponse?.factor_vendor_transition ?? null;
          didSaveRiskContext = true;
          if (factorVendorTransition?.target_factor_model && confirmedFactorModel !== formatUserFactorModel(nextFactorModel)) {
            // The vendor is saved when the switch publishes; until then the
            // saved vendor stays selected.
            setSettingsFactorModel(confirmedFactorModel);
          }
        } catch (error) {
          console.error('Error saving risk context settings:', error);
          nextSaveErrors.push({
            groupKey: 'riskContext',
            label: SETTINGS_SAVE_GROUP_META.riskContext.label,
            message: error?.message || 'Failed to save risk context settings.',
          });
        }
      }

      if (dirtyState.betaSource) {
        setSettingsSaveStepLabel(SETTINGS_SAVE_GROUP_META.betaSource.progressLabel);
        try {
          const betaSourceResponse = await updateBetaSource(nextBetaSource);
          if (!betaSourceResponse?.success) {
            throw new Error(betaSourceResponse?.message || 'Failed to save beta source settings.');
          }
          const nextAvailability = betaSourceResponse?.available_beta_sources ?? userData?.available_beta_sources ?? null;
          const confirmedBetaSource = normalizeBetaSource(betaSourceResponse?.beta_source ?? nextBetaSource);
          nextConfirmedState.betaSource = confirmedBetaSource;
          userDataUpdates.beta_source = confirmedBetaSource;
          userDataUpdates.available_beta_sources = nextAvailability;
          userDataUpdates.has_user_defined_betas = Object.keys(nextAvailability || {}).some((sourceId) => (
            String(sourceId).startsWith('watchlist:')
          ));
          didSaveBetaSource = true;
        } catch (error) {
          console.error('Error saving beta source settings:', error);
          nextSaveErrors.push({
            groupKey: 'betaSource',
            label: SETTINGS_SAVE_GROUP_META.betaSource.label,
            message: error?.message || 'Failed to save beta source settings.',
          });
        }
      }

      if (dirtyState.syncPreferences) {
        setSettingsSaveStepLabel(SETTINGS_SAVE_GROUP_META.syncPreferences.progressLabel);
        try {
          if (settingsFactorDataAutoSyncEnabled !== Boolean(settingsConfirmedState.factorDataAutoSyncEnabled)) {
            await saveFactorDataSyncPreferences(settingsFactorDataAutoSyncEnabled);
          }
          if (settingsPosSizingAutoSyncEnabled !== Boolean(settingsConfirmedState.posSizingAutoSyncEnabled)) {
            await savePosSizingSyncPreferences(settingsPosSizingAutoSyncEnabled);
          }
          dispatchAutoSyncUpdate({
            autoRefreshEnabled: settingsAutoRefreshEnabled,
            posSizingAutoSyncEnabled: settingsPosSizingAutoSyncEnabled,
            ...(settingsFactorDataAutoSyncEnabled !== Boolean(settingsConfirmedState.factorDataAutoSyncEnabled)
              ? { factorDataAutoSyncEnabled: settingsFactorDataAutoSyncEnabled } : {}),
          });
          setSyncControlState((current) => ({
            ...current,
            autoRefreshEnabled: settingsAutoRefreshEnabled,
            posSizingAutoSyncEnabled: settingsPosSizingAutoSyncEnabled,
            factorDataAutoSyncEnabled: settingsFactorDataAutoSyncEnabled,
          }));
          nextConfirmedState.autoRefreshEnabled = settingsAutoRefreshEnabled;
          nextConfirmedState.posSizingAutoSyncEnabled = settingsPosSizingAutoSyncEnabled;
          nextConfirmedState.factorDataAutoSyncEnabled = settingsFactorDataAutoSyncEnabled;
        } catch (error) {
          console.error('Error applying sync preferences:', error);
          nextSaveErrors.push({
            groupKey: 'syncPreferences',
            label: SETTINGS_SAVE_GROUP_META.syncPreferences.label,
            message: error?.message || 'Failed to apply sync preferences.',
          });
        }
      }

      if (
        didSaveRiskContext
        && normalizeUserFactorModel(nextConfirmedState.factorModel) === normalizeUserFactorModel(startingConfirmedState.factorModel)
        && nextConfirmedState.idioVolSource !== startingConfirmedState.idioVolSource
        && !didSaveFactorDataModelSelection
        && !didSaveBetaSource
        && typeof window !== 'undefined'
      ) {
        window.dispatchEvent(new CustomEvent(RISK_CONTEXT_REFRESH_REQUEST_EVENT, {
          detail: {
            idioVolSource: nextConfirmedState.idioVolSource,
          },
        }));
      }

      // A confirmed vendor switch is the user's approval of that vendor's
      // data. The server staged its re-check, so it is approved even when
      // another group failed: now, or once a running Watchlist change ends.
      // The Dashboard claims the request; elsewhere it is approved here.
      const factorVendorRecheck = didSaveRiskContext && Boolean(
        factorVendorTransition?.successor?.watchlist_revision_id
        || factorVendorTransition?.context?.available
      );
      let factorVendorSwitchOutcome = null;
      if (factorVendorRecheck && typeof window !== 'undefined') {
        const unclaimed = window.dispatchEvent(new CustomEvent(RISK_CONTEXT_REFRESH_REQUEST_EVENT, {
          detail: {
            factorVendorTransition,
          },
          cancelable: true,
        }));
        if (unclaimed) {
          factorVendorSwitchOutcome = await approveFactorVendorRecheck(
            factorVendorTransition,
            securityOperationSessionScopeToken,
          );
        } else {
          factorVendorSwitchOutcome = factorVendorTransition?.status === 'waiting_for_watchlist_update'
            ? 'waiting'
            : 'started';
        }
        if (factorVendorSwitchOutcome === 'started') {
          setStartedVendorSwitch({
            state: 'switching',
            revision_id: factorVendorTransition?.successor?.watchlist_revision_id,
            target_vendor: factorVendorTransition?.target_factor_model,
            current_vendor: factorVendorTransition?.factor_model,
          });
        }
      }

      if (Object.keys(userDataUpdates).length > 0) {
        onUserDataChange?.(userDataUpdates);
      }

      setSettingsConfirmedState(nextConfirmedState);

      if (nextSaveErrors.length > 0) {
        nextSaveErrors.forEach(({ groupKey }) => {
          revertSettingsGroupToConfirmed(groupKey, nextConfirmedState);
        });
        setSettingsSaveErrors(nextSaveErrors);
        if (factorVendorSwitchOutcome === 'failed' || factorVendorSwitchOutcome === 'waiting') {
          // Settings stays open for the other group; say the vendor is unchanged.
          setSettingsSnackbar({
            open: true,
            ...factorVendorSwitchSavedNotice(factorVendorTransition, factorVendorSwitchOutcome),
          });
        }
        return false;
      }

      const savedAnything = Object.values(dirtyState).some(Boolean);
      if (factorVendorSwitchOutcome) {
        setSettingsSnackbar({
          open: true,
          ...factorVendorSwitchSavedNotice(factorVendorTransition, factorVendorSwitchOutcome),
        });
      } else if (savedAnything) {
        setSettingsSnackbar({
          open: true,
          message: 'Settings saved.',
          severity: 'success',
        });
      }
      closeSettingsDialog();
      return true;
    } finally {
      setSettingsSaveStepLabel('');
      setSettingsSaving(false);
    }
  }, [
    closeSettingsDialog,
    dispatchAutoSyncUpdate,
    onUserDataChange,
    publishedContextStatus?.status,
    revertSettingsGroupToConfirmed,
    securityOperationSessionScopeToken,
    settingsAutoRefreshEnabled,
    settingsAxiomaModel,
    settingsBarraModel,
    settingsBetaSource,
    settingsConfirmedState,
    settingsDirtyState,
    settingsFactorModel,
    settingsHasPendingChanges,
    settingsIdioVolSource,
    settingsPosSizingAutoSyncEnabled,
    settingsFactorDataAutoSyncEnabled,
    settingsSaving,
    userData?.available_beta_sources,
    userData?.watchlist_version_count,
  ]);
  const handleOpenSettings = () => {
    beginAdminUsersOpenGeneration();
    setSettingsConfirmedState(externalConfirmedSettingsState);
    applySettingsDraftFromConfirmedState(externalConfirmedSettingsState);
    setSettingsSaveErrors([]);
    setSettingsSaveStepLabel('');
    setSettingsInfoAnchorEl(null);
    setSettingsInfoContent('');
    setMsiResolutionFocusTarget(null);
    setM365CallbackResult(null);
    setSettingsTab('appearance');
    setExecutionSourceRefreshToken((current) => current + 1);
    setSettingsOpen(true);
  };

  useEffect(() => {
    const handleUserSettingsOpen = (event) => {
      const tab = String(event?.detail?.tab || 'appearance').trim() || 'appearance';
      beginAdminUsersOpenGeneration();
      setSettingsConfirmedState(externalConfirmedSettingsState);
      applySettingsDraftFromConfirmedState(externalConfirmedSettingsState);
      setSettingsSaveErrors([]);
      setSettingsSaveStepLabel('');
      setSettingsInfoAnchorEl(null);
      setSettingsInfoContent('');
      setM365CallbackResult(null);
      setSettingsTab(tab);
      setMsiResolutionFocusTarget(event?.detail?.focusTarget || null);
      setExecutionSourceRefreshToken((current) => current + 1);
      setSettingsOpen(true);
      if (tab === 'msi' && typeof onRefreshMsiResolutionCenter === 'function') {
        void onRefreshMsiResolutionCenter({ force: true });
      }
    };
    window.addEventListener(USER_SETTINGS_OPEN_EVENT, handleUserSettingsOpen);
    return () => {
      window.removeEventListener(USER_SETTINGS_OPEN_EVENT, handleUserSettingsOpen);
    };
  }, [
    applySettingsDraftFromConfirmedState,
    beginAdminUsersOpenGeneration,
    externalConfirmedSettingsState,
    onRefreshMsiResolutionCenter,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('m365_watchlist')) {
      return;
    }
    const callbackResult = readWatchlistM365CallbackResult(location.search);
    navigate(
      {
        pathname: location.pathname,
        search: removeWatchlistM365CallbackResult(location.search),
      },
      { replace: true },
    );
    if (!callbackResult) {
      return;
    }
    beginAdminUsersOpenGeneration();
    setSettingsConfirmedState(externalConfirmedSettingsState);
    applySettingsDraftFromConfirmedState(externalConfirmedSettingsState);
    setSettingsSaveErrors([]);
    setSettingsSaveStepLabel('');
    setSettingsInfoAnchorEl(null);
    setSettingsInfoContent('');
    setMsiResolutionFocusTarget(null);
    setM365CallbackResult(callbackResult);
    setSettingsTab('integrations');
    setExecutionSourceRefreshToken((current) => current + 1);
    setSettingsOpen(true);
  }, [
    applySettingsDraftFromConfirmedState,
    beginAdminUsersOpenGeneration,
    externalConfirmedSettingsState,
    location.pathname,
    location.search,
    navigate,
  ]);

  const handleDiscardSettingsChanges = useCallback(() => {
    if (settingsSaving || !settingsHasPendingChanges) {
      return;
    }
    applySettingsDraftFromConfirmedState(settingsConfirmedState);
    setSettingsSaveErrors([]);
    setSettingsSaveStepLabel('');
    setSettingsInfoAnchorEl(null);
    setSettingsInfoContent('');
  }, [
    applySettingsDraftFromConfirmedState,
    settingsConfirmedState,
    settingsHasPendingChanges,
    settingsSaving,
  ]);

  const handleCloseSettings = useCallback(async () => {
    if (settingsSaving) {
      return;
    }
    if (!settingsHasPendingChanges) {
      closeSettingsDialog();
      return;
    }
    await handleSaveSettings();
  }, [closeSettingsDialog, handleSaveSettings, settingsHasPendingChanges, settingsSaving]);
  const closeSettingsBeforeNavigation = useCallback(async () => {
    if (settingsSaving) {
      return false;
    }
    if (settingsHasPendingChanges) {
      const saved = await handleSaveSettings();
      if (!saved) {
        return false;
      }
    } else {
      closeSettingsDialog();
    }
    return true;
  }, [
    closeSettingsDialog,
    handleSaveSettings,
    settingsHasPendingChanges,
    settingsSaving,
  ]);
  const handleOpenLinkedExcelWatchlist = useCallback(async () => {
    const canNavigate = await closeSettingsBeforeNavigation();
    if (!canNavigate) {
      return false;
    }
    navigate('/dashboard/watchlist');
    return true;
  }, [
    closeSettingsBeforeNavigation,
    navigate,
  ]);
  const handleOpenWatchlistRemovalReview = useCallback(async (detail) => {
    const navigationState = createWatchlistReviewNavigationState(detail);
    if (!navigationState) {
      return false;
    }
    const canNavigate = await closeSettingsBeforeNavigation();
    if (!canNavigate) {
      return false;
    }
    navigate('/dashboard/watchlist', { state: navigationState });
    return true;
  }, [closeSettingsBeforeNavigation, navigate]);
  // A confirmed factor date approves its Watchlist re-check, as a confirmed
  // vendor switch does: Settings closes, saving any other changes first, and
  // the app bar shows the re-check's progress.
  const selectSettingsFactorDataset = uploadContext.onSelectFactorDataset;
  const handleLoadSettingsFactorDataset = useCallback(async (selection) => {
    const canLeave = await closeSettingsBeforeNavigation();
    if (!canLeave) {
      return;
    }
    void selectSettingsFactorDataset?.(selection);
  }, [closeSettingsBeforeNavigation, selectSettingsFactorDataset]);
  // Loading new factor data from Data status leaves Settings the same way;
  // its details, showing the current and available data, are the question.
  const closeSettingsFactorDataDetails = uploadContext.onFactorDataDetailsOpenChange;
  const handleLoadSettingsFactorDataUpdate = useCallback(async () => {
    closeSettingsFactorDataDetails?.(false);
    const canLeave = await closeSettingsBeforeNavigation();
    if (!canLeave) {
      return;
    }
    window.dispatchEvent(new CustomEvent(FACTOR_DATA_REFRESH_REQUEST_EVENT));
  }, [closeSettingsBeforeNavigation, closeSettingsFactorDataDetails]);
  const handleSelectSettingsFactorModel = useCallback((value) => {
    clearSettingsApplyErrors();
    acknowledgeVendorSwitchNotice();
    setSettingsFactorModel(value);
  }, [acknowledgeVendorSwitchNotice, clearSettingsApplyErrors]);
  const handleSelectSettingsAxiomaModel = useCallback((value) => {
    clearSettingsApplyErrors();
    acknowledgeVendorSwitchNotice();
    setSettingsAxiomaModel(value);
  }, [acknowledgeVendorSwitchNotice, clearSettingsApplyErrors]);
  const handleSelectSettingsBarraModel = useCallback((value) => {
    clearSettingsApplyErrors();
    acknowledgeVendorSwitchNotice();
    setSettingsBarraModel(value);
  }, [acknowledgeVendorSwitchNotice, clearSettingsApplyErrors]);
  const handleSelectSettingsIdioVolSource = useCallback((value) => {
    clearSettingsApplyErrors();
    setSettingsIdioVolSource(value);
  }, [clearSettingsApplyErrors]);
  const handleSelectSettingsBetaSource = useCallback((value) => {
    clearSettingsApplyErrors();
    setSettingsBetaSource(value);
  }, [clearSettingsApplyErrors]);
  // Auto-saves immediately (like theme/compact mode) rather than gating
  // behind the Save button used by beta source.
  const handleSelectIdleLogoutMinutes = useCallback((value) => {
    setIdleLogoutMinutes(value);
  }, [setIdleLogoutMinutes]);
  // W8 (2026-08-04) — auto-saves immediately for the same reason as
  // idleLogoutMinutes: the change is a scalar user pref piggybacking on the
  // debounced `persistAppearancePreferences` write in App.jsx.
  const handleSelectNewsRecencyHours = useCallback((value) => {
    if (typeof setNewsRecencyHours === 'function') {
      setNewsRecencyHours(value);
    }
  }, [setNewsRecencyHours]);
  // Multi-select toggle with `all`-mutex behavior.
  // Clicking `all` → set `['all']`.
  // Clicking a specific → strip `all`, toggle the specific.
  // Empty after removal → snap back to `['all']`.
  const handleToggleDeepDiveFocusArea = useCallback((value) => {
    if (typeof setDeepDiveFocusAreas !== 'function') return;
    const current = Array.isArray(deepDiveFocusAreas) ? deepDiveFocusAreas : ['all'];
    if (value === 'all') {
      setDeepDiveFocusAreas(['all']);
      return;
    }
    const withoutAll = current.filter((v) => v !== 'all');
    const next = withoutAll.includes(value)
      ? withoutAll.filter((v) => v !== value)
      : [...withoutAll, value];
    setDeepDiveFocusAreas(next.length === 0 ? ['all'] : next);
  }, [deepDiveFocusAreas, setDeepDiveFocusAreas]);
  const deepDiveFocusAreasSet = useMemo(() => (
    new Set(Array.isArray(deepDiveFocusAreas) ? deepDiveFocusAreas : ['all'])
  ), [deepDiveFocusAreas]);

  const profileMenuItems = [
    {
      label: linkedProviderIds.includes('password') ? 'Reset Password' : 'Set Password',
      action: handleResetPassword,
    },
    { label: 'Logout', color: 'error', action: handleLogout },
  ];

  // Check if we're in production environment
  const appMode = getAppMode();
  const isProduction = isAppProductionMode();

  // Fixed amber hue, mode-tuned lightness -- see getAdminAccentColor's own
  // comment for why this isn't just theme.palette.warning.main.
  const adminAccentColor = useMemo(() => getAdminAccentColor(themeId), [themeId]);
  const msiAppBarStyles = buildMsiAppBarStyles(themeId);
  const ADMIN_TOOLTIP_TEXT = 'Navigate to User Settings → Admin to manage member registration and privileges.';
  
  const navItems = [
    { label: 'Risk Overview', path: '/dashboard/overview', icon: InsightsOutlinedIcon },
    { label: 'Watchlist', path: '/dashboard/watchlist', icon: VisibilityOutlinedIcon },
    { label: 'Position Sizing', path: '/dashboard/pos-sizing', icon: TrackChangesOutlinedIcon },
    { label: 'Optimizer', path: '/dashboard/optimizer', icon: Inventory2OutlinedIcon },
  ];

  navItems.push({ label: 'Performance', path: '/dashboard/performance', icon: QueryStatsOutlinedIcon });
  if (isAdmin && isFdlAdminEnabled()) {
    navItems.push({ label: 'Factor Data', path: '/admin/factor-data', icon: StorageRoundedIcon, adminOnly: true });
  }
  if (isAdmin && isMsiAdminEnabled()) {
    navItems.push({ label: 'Security Identity', path: '/admin/security-identity', icon: StorageRoundedIcon, adminOnly: true });
  }
  if (isAdmin) {
    navItems.push({ label: 'MOSEK Licence', path: '/admin/mosek-license', icon: VpnKeyOutlinedIcon, adminOnly: true });
    navItems.push({ label: 'Market Data', path: '/admin/market-data', icon: MonitorHeartOutlinedIcon, adminOnly: true });
  }
  const setupGuideTargetPath = setupGuideState?.targetTab
    ? `/dashboard/${setupGuideState.targetTab}`
    : null;
  const setupGuideStep = setupGuideState?.step || null;
  const showSetupGuideOnTabs = Boolean(
    (setupGuideStep === 'watchlist' || !setupGuideDismissed) &&
    setupGuideTargetPath &&
    location.pathname !== setupGuideTargetPath
  );

  const sharedBorderColor = (theme) => alpha(
    theme.palette.divider,
    theme.palette.mode === 'dark' ? 0.34 : 0.62,
  );
  const sharedSurfaceColor = (theme) => alpha(
    theme.palette.background.paper,
    theme.palette.mode === 'dark' ? 0.46 : 0.84,
  );
  const utilityIconSx = appBarActionButtonSx;
  const pillButtonSx = {
    ...msiAppBarStyles.profileControl,
    minHeight: 30,
    px: 1.05,
    borderRadius: 999,
    border: '1px solid',
    borderColor: (theme) => sharedBorderColor(theme),
    backgroundColor: (theme) => sharedSurfaceColor(theme),
    color: 'text.secondary',
    textTransform: 'none',
    fontSize: '0.74rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    '&:hover': {
      backgroundColor: (theme) => alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.36 : 0.7),
      borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.2),
      color: 'text.primary',
    },
  };
  const dialogPaperSx = {
    borderRadius: 3,
    border: '1px solid',
    borderColor: (theme) => sharedBorderColor(theme),
    minHeight: { xs: 520, sm: 610 },
    maxHeight: '85vh',
    backgroundImage: 'none',
    backgroundColor: (theme) => theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.98)
      : 'rgb(var(--c-elevated))',
    boxShadow: (theme) => theme.palette.mode === 'dark'
      ? '0 26px 64px rgba(0, 0, 0, 0.46)'
      : '0 24px 60px rgba(15, 23, 42, 0.12)',
    ...msiAppBarStyles.dialogPaper,
  };
  const resolvedMsiResolutionCenter = useMemo(
    () => normalizeMsiResolutionCenter(msiResolutionCenter, userData?.factor_model),
    [msiResolutionCenter, userData?.factor_model],
  );
  // Input-issue rows are server-owned now; consume the read model verbatim.
  const displayMsiResolutionCenter = resolvedMsiResolutionCenter;
  // A new user's first published Watchlist gets one pointer to Security
  // Resolution. Dismissing it, or opening that tab at any time, hides it for
  // good: the choice is saved with the user's preferences on the server.
  // It points at Settings in the side bar, or at the menu button that opens
  // the side bar on narrow screens.
  const [sidebarSettingsEl, setSidebarSettingsEl] = useState(null);
  const [navigationMenuButtonEl, setNavigationMenuButtonEl] = useState(null);
  const securityResolutionHintAnchorEl = useCompactSettingsNavigation
    ? navigationMenuButtonEl
    : sidebarSettingsEl;
  const [securityResolutionHintDismissedUid, setSecurityResolutionHintDismissedUid] = useState('');
  const securityResolutionHintDismissed = (
    userData?.appearance_preferences?.securityResolutionHintDismissed === true
    || (Boolean(user?.uid) && securityResolutionHintDismissedUid === user.uid)
  );
  const dismissSecurityResolutionHint = useCallback(() => {
    if (!user?.uid) {
      return;
    }
    setSecurityResolutionHintDismissedUid(user.uid);
    void persistAppearancePreferences({ securityResolutionHintDismissed: true });
  }, [user?.uid]);
  useEffect(() => {
    if (settingsOpen && settingsTab === 'msi' && !securityResolutionHintDismissed) {
      dismissSecurityResolutionHint();
    }
  }, [dismissSecurityResolutionHint, securityResolutionHintDismissed, settingsOpen, settingsTab]);
  const showSecurityResolutionHint = (
    userData?.watchlist_version_count === 1
    && !securityResolutionHintDismissed
    && !settingsOpen
  );
  const handleOpenSecurityResolutionFromHint = () => {
    window.dispatchEvent(new CustomEvent(USER_SETTINGS_OPEN_EVENT, { detail: { tab: 'msi' } }));
  };
  // Away from the next setup step's page, a bubble points at that page's tab
  // in the side menu. The portfolio step comes after Security Resolution, so
  // it waits for that bubble, and it hides once the user has engaged with the
  // step's own page guide. Closing the Watchlist step hides it for this visit
  // only, because an empty Watchlist always keeps its entry point; closing the
  // portfolio step ends the setup guide.
  const [setupGuideTargetEl, setSetupGuideTargetEl] = useState(null);
  const [watchlistStepHintHiddenUid, setWatchlistStepHintHiddenUid] = useState('');
  const setupStepEngaged = setupGuideStep === 'watchlist'
    ? Boolean(setupGuideState?.watchlistUploadInteracted)
    : Boolean(setupGuideState?.portfolioImportInteracted);
  const showSetupStepHint = Boolean(
    showSetupGuideOnTabs
    && !setupStepEngaged
    && !(setupGuideStep === 'watchlist' && Boolean(user?.uid) && watchlistStepHintHiddenUid === user.uid)
    && !showSecurityResolutionHint
    && !settingsOpen
  );
  const handleDismissSetupStepHint = () => {
    if (setupGuideStep === 'portfolio') {
      dismissSetupGuide?.();
      return;
    }
    setWatchlistStepHintHiddenUid(user?.uid || '');
  };
  const settingsWorkspacePaperSx = {
    width: '100%',
    maxWidth: '100vw',
    overflow: 'hidden',
    backgroundImage: 'none',
    backgroundColor: 'rgb(var(--c-deep) / 0.985)',
    ...msiAppBarStyles.settingsWorkspacePaper,
    background: getAppCanvasBackground,
  };
  const settingsPanelSurfaceColor = (theme) => (
    msiAppBarStyles.settingsPanelSurface
      || (theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.56)
      : alpha('#ffffff', 0.88))
  );
  const settingsMutedSurfaceColor = (theme) => (
    msiAppBarStyles.settingsMutedSurface
      || (theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.38)
      : alpha('#ffffff', 0.72))
  );
  const settingsSectionCardSx = {
    borderRadius: 2,
    border: '1px solid',
    borderColor: (theme) => sharedBorderColor(theme),
    backgroundColor: (theme) => settingsPanelSurfaceColor(theme),
    boxShadow: 'none',
    overflow: 'hidden',
    ...msiAppBarStyles.settingsSectionCard,
  };
  const settingsRowsContainerSx = {
    display: 'grid',
    '& > :not(style) + :not(style)': {
      borderTop: '1px solid',
      borderColor: (theme) => sharedBorderColor(theme),
    },
  };
  const appStyleSettingsTabSx = msiAppBarStyles.settingsTabs?.['& .MuiTab-root'] || {};
  const appStyleSettingsTabHoverSx = appStyleSettingsTabSx['&:hover'] || {};
  const appStyleSettingsSelectedTabSx = msiAppBarStyles.settingsTabs?.['& .Mui-selected'] || {};
  const settingsTopTabsSx = {
    width: '100%',
    minHeight: 0,
    '& .MuiTabs-scroller': {
      overflowY: 'visible !important',
    },
    '& .MuiTabs-flexContainer': {
      gap: { xs: 0.6, md: 0.7 },
      alignItems: 'stretch',
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTab-root': {
      width: { xs: 'auto', md: '100%' },
      maxWidth: 'none',
      minHeight: 40,
      minWidth: { xs: 132, sm: 148, md: 0 },
      justifyContent: { xs: 'center', md: 'flex-start' },
      alignItems: 'center',
      gap: 0.85,
      px: { xs: 1.15, md: 1.15 },
      py: 0.8,
      borderRadius: 2,
      border: '1px solid',
      borderColor: (theme) => sharedBorderColor(theme),
      backgroundColor: (theme) => settingsMutedSurfaceColor(theme),
      color: 'text.secondary',
      fontSize: { xs: '0.72rem', md: '0.78rem' },
      fontWeight: 650,
      lineHeight: 1.2,
      letterSpacing: 0,
      textTransform: 'none',
      textAlign: { xs: 'center', md: 'left' },
      whiteSpace: 'normal',
      transition: 'background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, color 140ms ease',
      ...appStyleSettingsTabSx,
      '& .MuiTab-iconWrapper': {
        marginRight: 0,
        marginBottom: '0 !important',
      },
      '&:hover': {
        color: 'text.primary',
        borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.18),
        backgroundColor: (theme) => alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.38 : 0.72),
        ...appStyleSettingsTabHoverSx,
      },
    },
    '& .Mui-selected': {
      color: 'text.primary',
      borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.58 : 0.26),
      backgroundColor: (theme) => theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.24)
        : alpha(theme.palette.primary.main, 0.12),
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 10px 24px rgba(0, 0, 0, 0.22)'
        : '0 8px 18px rgba(15, 23, 42, 0.08)',
      ...appStyleSettingsSelectedTabSx,
    },
  };
  const getSettingsChoiceChipSx = (selected, { monospace = false } = {}) => ({
    height: 32,
    borderRadius: 999,
    borderColor: (theme) => selected
      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.58 : 0.26)
      : sharedBorderColor(theme),
    backgroundColor: (theme) => selected
      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.12)
      : settingsMutedSurfaceColor(theme),
    color: 'text.primary',
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: 0,
    fontFamily: monospace ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
    '& .MuiChip-label': {
      px: 1.15,
    },
    '&:hover': {
      backgroundColor: (theme) => selected
        ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.16)
        : alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.34 : 0.72),
    },
    ...msiAppBarStyles.settingsChoice?.(selected),
  });
  const getSettingsChoiceButtonSx = (selected) => ({
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    minHeight: 74,
    px: 1.4,
    py: 1.15,
    borderRadius: 2,
    border: '1px solid',
    borderColor: (theme) => selected
      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.58 : 0.26)
      : sharedBorderColor(theme),
    backgroundColor: (theme) => selected
      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.12)
      : settingsMutedSurfaceColor(theme),
    color: 'text.primary',
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
      borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.62 : 0.28),
      backgroundColor: (theme) => selected
        ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.14)
        : alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.34 : 0.72),
    },
    ...msiAppBarStyles.settingsChoice?.(selected),
  });
  const activeSettingsTabItem = SETTINGS_TAB_ITEMS.find((item) => item.value === settingsTab);
  const ActiveSettingsTabIcon = activeSettingsTabItem?.icon || SettingsOutlinedIcon;
  const settingsPageIntro = (
    <Box component="header" sx={{ display: 'flex', alignItems: 'center', gap: 1.35, minWidth: 0 }}>
      <Box
        aria-hidden="true"
        sx={{
          width: 42,
          height: 42,
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.22),
          borderRadius: 2,
          backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
          color: 'primary.main',
        }}
      >
        <ActiveSettingsTabIcon sx={{ fontSize: 21 }} />
      </Box>
      <Box sx={{ display: 'grid', gap: 0.35, minWidth: 0 }}>
        <Typography component="h1" sx={{ ...SETTINGS_DISPLAY_TITLE_SX, fontSize: { xs: '1.1rem', md: '1.24rem' }, color: 'text.primary' }}>
          {activeSettingsTabItem?.label || 'User Settings'}
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.73rem', md: '0.78rem' }, color: 'text.secondary', lineHeight: 1.5 }}>
          {SETTINGS_TAB_DESCRIPTIONS[settingsTab] || ''}
        </Typography>
      </Box>
    </Box>
  );
  const renderSettingsSwitch = (checked, onChange, {
    disabled = false,
    statusLabel = checked ? 'On' : 'Off',
  } = {}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
      <Typography sx={{ fontSize: '0.69rem', color: 'text.secondary' }}>
        {statusLabel}
      </Typography>
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
    </Box>
  );
  const renderThemeOption = (themeOption) => {
    const isSelected = themeId === themeOption.id;
    const { colors } = themeOption;
    const selectThisTheme = () => setThemeId(themeOption.id);
    return (
      <Box
        key={themeOption.id}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        onClick={selectThisTheme}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            selectThisTheme();
          }
        }}
        sx={{
          cursor: 'pointer',
          width: '100%',
          minWidth: 0,
          p: 0.9,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          borderRadius: 2,
          border: '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          backgroundColor: (theme) => (isSelected
            ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.17 : 0.07)
            : theme.palette.background.paper),
          boxShadow: isSelected ? (theme) => `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.34)}` : 'none',
          transition: 'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
          '&:hover': { borderColor: 'primary.main', transform: 'translateY(-1px)' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <Box
          sx={{
            height: 64,
            borderRadius: 1.25,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            backgroundColor: colors.bgBase,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Title bar mockup: window-control dots on a raised strip. */}
          <Box
            sx={{
              height: 11,
              flexShrink: 0,
              backgroundColor: colors.surface,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              px: '5px',
            }}
          >
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: colors.textSecondary }} />
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: colors.textSecondary }} />
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: colors.textSecondary }} />
          </Box>
          {/* Body mockup: an accent-filled active control plus text-line bars. */}
          <Box sx={{ flex: 1, p: '6px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
            <Box sx={{ width: '55%', height: 6, borderRadius: '999px', backgroundColor: colors.accent }} />
            <Box sx={{ width: '80%', height: 2, borderRadius: '1px', backgroundColor: colors.textSecondary }} />
            <Box sx={{ width: '60%', height: 2, borderRadius: '1px', backgroundColor: colors.textSecondary }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.6 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: isSelected ? 750 : 600, color: 'text.primary' }}>
            {themeOption.label}
          </Typography>
          {isSelected && <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 15 }} />}
        </Box>
      </Box>
    );
  };
  const renderColorOverrideControl = (token) => {
    const overrideHex = colorOverrides?.[token];
    const isOverridden = Boolean(overrideHex);
    const currentHex = overrideHex || getThemeColor(themeId, token);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
        <Box
          component="span"
          sx={{
            fontSize: '0.69rem',
            fontFamily: 'monospace',
            color: 'text.secondary',
            textTransform: 'uppercase',
            minWidth: 64,
            textAlign: 'right',
          }}
        >
          {currentHex}
        </Box>
        <Box
          component="input"
          type="color"
          aria-label={`${token} color`}
          value={currentHex}
          onChange={(event) => setColorOverride(token, event.target.value)}
          sx={{
            width: 34,
            height: 26,
            p: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '2px',
            background: 'none',
            cursor: 'pointer',
            '&::-webkit-color-swatch-wrapper': { p: '2px' },
            '&::-webkit-color-swatch': { border: 'none', borderRadius: '1px' },
          }}
        />
        <Button
          size="small"
          variant="text"
          disabled={!isOverridden}
          onClick={() => resetColorOverride(token)}
          sx={{ minWidth: 0, px: 0.75, fontSize: '0.66rem', textTransform: 'none' }}
        >
          Reset
        </Button>
      </Box>
    );
  };
  const hasAnyColorOverride = OVERRIDABLE_TOKENS.some(({ token }) => Boolean(colorOverrides?.[token]));
  const hasAnySectorOverride = Boolean(sectorPaletteOverride && Object.keys(sectorPaletteOverride).length > 0);
  const renderSectorSwatch = (color, index) => {
    const isOverridden = Boolean(sectorPaletteOverride?.[index]);
    return (
      <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
        <Box
          component="input"
          type="color"
          aria-label={`Attribute color ${index + 1}`}
          value={color}
          onChange={(event) => setSectorColor(index, event.target.value)}
          sx={{
            width: 40,
            height: 28,
            p: 0,
            border: '1px solid',
            borderColor: isOverridden ? 'primary.main' : 'divider',
            borderRadius: '2px',
            background: 'none',
            cursor: 'pointer',
            '&::-webkit-color-swatch-wrapper': { p: '2px' },
            '&::-webkit-color-swatch': { border: 'none', borderRadius: '1px' },
          }}
        />
        <Button
          size="small"
          variant="text"
          disabled={!isOverridden}
          onClick={() => resetSectorColor(index)}
          sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.6rem', textTransform: 'none', lineHeight: 1.4 }}
        >
          Reset
        </Button>
      </Box>
    );
  };
  // Hidden until we build proper proportional scaling instead of this binary
  // toggle. Flip to true to bring the setting back.
  const SHOW_COMPACT_MODE_TOGGLE = false;
  const renderAppearanceSettings = () => (
    <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto', display: 'grid', gap: 3 }}>
      {settingsPageIntro}
      <SettingsSection
        title="Workspace display"
        description="Control the base color theme for the rest of the app."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={settingsRowsContainerSx}>
          <SettingsRow
            label="Theme"
            description="Pick the base color theme for the workspace."
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(4, minmax(112px, 1fr))',
                },
                gap: 1,
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                width: '100%',
                maxWidth: 580,
              }}
            >
              {THEME_LIST.map(renderThemeOption)}
            </Box>
          </SettingsRow>
          {SHOW_COMPACT_MODE_TOGGLE && (
            <SettingsRow
              label="Compact mode"
              description="Reduce spacing so more tables and controls fit on screen."
            >
              {renderSettingsSwitch(compactMode, toggleCompactMode)}
            </SettingsRow>
          )}
        </Box>
      </SettingsSection>
      <SettingsSection
        title="Semantic colors"
        description="Recolor the highlight colors used across tables and charts. Changes are saved on this device."
        paperSx={settingsSectionCardSx}
        action={hasAnyColorOverride ? (
          <Button
            size="small"
            variant="text"
            onClick={resetAllColorOverrides}
            sx={{ minWidth: 0, px: 0.75, fontSize: '0.68rem', textTransform: 'none' }}
          >
            Reset all
          </Button>
        ) : null}
      >
        <Box sx={settingsRowsContainerSx}>
          {OVERRIDABLE_TOKENS.map(({ token, label, description }) => (
            <SettingsRow key={token} label={label} description={description}>
              {renderColorOverrideControl(token)}
            </SettingsRow>
          ))}
        </Box>
      </SettingsSection>
      <SettingsSection
        title="Attribute palette"
        description="Colors assigned to attribute categories in the position-sizing scatter plot, in order. Saved on this device."
        paperSx={settingsSectionCardSx}
        action={hasAnySectorOverride ? (
          <Button
            size="small"
            variant="text"
            onClick={resetSectorPalette}
            sx={{ minWidth: 0, px: 0.75, fontSize: '0.68rem', textTransform: 'none' }}
          >
            Reset all
          </Button>
        ) : null}
      >
        <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.6, display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
          {sectorPalette.map((color, index) => renderSectorSwatch(color, index))}
        </Box>
      </SettingsSection>
    </Box>
  );
  const renderFactorDataSettings = () => (
    <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto', display: 'grid', gap: 3 }}>
      {settingsPageIntro}

      {uploadContext.factorDataFreshnessSummary?.visible && (
        <SettingsSection
          title="Data status"
          description="Review the factor data used by your Watchlist and portfolio."
          paperSx={settingsSectionCardSx}
        >
          <Box
            sx={{
              px: { xs: 1.4, md: 1.8 },
              py: 1.35,
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <FactorDataStatusControl
              summary={uploadContext.factorDataFreshnessSummary}
              onLoadClick={handleLoadSettingsFactorDataUpdate}
              loadDisabled={uploadContext.factorDataLoadDisabled}
              loadInProgress={uploadContext.factorDataLoadInProgress}
              detailsOpen={uploadContext.factorDataDetailsOpen}
              onDetailsOpenChange={uploadContext.onFactorDataDetailsOpenChange}
            />
          </Box>
          {uploadContext.factorDatasetCatalogue?.enabled && (
            <Box sx={{ px: { xs: 1.4, md: 1.8 }, pb: 1.5 }}>
              <FactorDatasetSelector
                catalogue={uploadContext.factorDatasetCatalogue}
                onLoad={handleLoadSettingsFactorDataset}
                disabled={uploadContext.factorDataLoadDisabled}
                busy={uploadContext.factorDataLoadInProgress}
                blockedReason={uploadContext.factorDataLoadBlockedReason}
                updateProgress={appBarFactorUpdateProgress ? (
                  <SecurityOperationProgress
                    statusDoc={securityOperationStatus}
                    contextUpdate={appBarFactorUpdateProgress}
                    compact
                    onOpenRemovalReview={handleOpenWatchlistRemovalReview}
                  />
                ) : null}
              />
            </Box>
          )}
        </SettingsSection>
      )}

      <SettingsSection
        title="Model selection"
        description="Choose the default model provider plus the vendor-specific factor-data variants used when calculations run."
        infoKey="model_selection"
        onInfoClick={handleOpenSettingsSectionInfo}
        paperSx={settingsSectionCardSx}
      >
        <Box sx={settingsRowsContainerSx}>
          {settingsVendorSwitchNotice && (
            <Alert
              severity="info"
              data-testid="factor-vendor-switch-status"
              onClose={settingsVendorSwitchNotice.state === 'cancelled'
                ? acknowledgeVendorSwitchNotice
                : undefined}
            >
              {settingsVendorSwitchRunning
                ? `${factorVendorSwitchingText(settingsVendorSwitchNotice.target_vendor)} ${
                  settingsVendorSwitchNotice.state === 'review'
                    ? 'Review the Watchlist changes to finish the switch.'
                    : 'Your Watchlist switches when its check completes.'
                }`
                : `The switch to ${formatUserFactorModel(settingsVendorSwitchNotice.target_vendor)} was cancelled; your factor data source is still ${formatUserFactorModel(settingsVendorSwitchNotice.current_vendor)}.`}
            </Alert>
          )}
          <SettingsRow
            label="Model provider"
            description="Pick the active risk model family used by default across the workspace."
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1,
                width: '100%',
                maxWidth: 430,
              }}
            >
              {FACTOR_MODEL_OPTIONS.map((option) => (
                <Tooltip key={option.value} title={option.tooltip}>
                  <Box component="span" sx={{ display: 'block', width: '100%' }}>
                    <SettingsChoiceButton
                      label={option.value}
                      description={option.tooltip}
                      disabled={settingsVendorSwitchRunning}
                      onClick={settingsVendorSwitchRunning
                        ? undefined
                        : () => handleSelectSettingsFactorModel(option.value)}
                      sx={getSettingsChoiceButtonSx(settingsFactorModel === option.value)}
                    />
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </SettingsRow>
          <SettingsRow
            label="Axioma model"
            description="Set the default Axioma factor-data package used whenever Axioma is active."
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {AXIOMA_FACTOR_DATA_MODEL_OPTIONS.map((option) => (
                <Tooltip key={option.value} title={option.description}>
                  <Chip
                    clickable={!settingsVendorSwitchRunning}
                    disabled={settingsVendorSwitchRunning}
                    label={option.label}
                    variant="outlined"
                    onClick={settingsVendorSwitchRunning
                      ? undefined
                      : () => handleSelectSettingsAxiomaModel(option.value)}
                    sx={getSettingsChoiceChipSx(settingsAxiomaModel === option.value, { monospace: true })}
                  />
                </Tooltip>
              ))}
            </Box>
          </SettingsRow>
          <SettingsRow
            label="Barra model"
            description="Set the default Barra factor-data package used whenever Barra is active."
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {BARRA_FACTOR_DATA_MODEL_OPTIONS.map((option) => (
                <Tooltip
                  key={option.value}
                  title={option.disabled ? (option.tooltip || BARRA_SHORT_HORIZON_COMING_SOON_TOOLTIP) : option.description}
                >
                  <Box component="span">
                    <Chip
                      clickable={!option.disabled && !settingsVendorSwitchRunning}
                      disabled={Boolean(option.disabled) || settingsVendorSwitchRunning}
                      label={option.label}
                      variant="outlined"
                      onClick={option.disabled || settingsVendorSwitchRunning
                        ? undefined
                        : () => handleSelectSettingsBarraModel(option.value)}
                      sx={getSettingsChoiceChipSx(settingsBarraModel === option.value, { monospace: true })}
                    />
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </SettingsRow>
        </Box>
      </SettingsSection>

      <SettingsSection
        title="Idio volatility"
        description="Risk calculations currently use the standard SpecificRisk source."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={settingsRowsContainerSx}>
          <SettingsRow
            label="Default source"
            description={settingsAiIdioSupported
              ? 'Barra AI overlays SpecificRisk only and falls back to standard values whenever predictions are missing.'
              : AI_IDIO_VOL_DISABLED_REASON}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1,
                width: '100%',
                maxWidth: 430,
              }}
            >
              {IDIO_VOL_SOURCE_OPTIONS.map((option) => {
                const aiOptionDisabled = option.value === IDIO_VOL_SOURCE_AI && !settingsAiIdioSupported;
                return (
                  <Tooltip
                    key={option.value}
                    title={aiOptionDisabled ? AI_IDIO_VOL_DISABLED_REASON : option.tooltip}
                  >
                    <Box component="span" sx={{ display: 'block', width: '100%' }}>
                      <SettingsChoiceButton
                        label={option.label}
                        description={aiOptionDisabled ? AI_IDIO_VOL_DISABLED_REASON : option.tooltip}
                        disabled={aiOptionDisabled}
                        onClick={() => {
                          if (!aiOptionDisabled) {
                            handleSelectSettingsIdioVolSource(option.value);
                          }
                        }}
                        sx={getSettingsChoiceButtonSx(settingsIdioVolSource === option.value)}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </SettingsRow>
        </Box>
      </SettingsSection>

    </Box>
  );
  const renderMachineLearningSettings = () => (
    <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto', display: 'grid', gap: 3 }}>
      {settingsPageIntro}
      <SettingsSection
        title="Active machine learning configuration"
        description="This page is read-only. Model selection and idio-vol defaults are configured from Factor Data and summarized here."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={settingsRowsContainerSx}>
          <SettingsRow
            label="Current status"
            description="Machine-learning behavior changes automatically when Factor Data settings change."
          >
            <Alert
              severity={!settingsAiIdioSupported || settingsIdioVolSource !== IDIO_VOL_SOURCE_AI ? 'info' : 'success'}
              variant="outlined"
              sx={{ width: '100%', py: 0.15 }}
            >
              {!settingsAiIdioSupported
                ? 'AI is unavailable'
                : settingsIdioVolSource === IDIO_VOL_SOURCE_AI
                  ? 'AI is active'
                  : 'Standard is active'}
            </Alert>
          </SettingsRow>
          <SettingsRow
            label="Model provider"
            description="The active provider determines whether AI idio-vol overlays are available."
          >
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Chip
                label={formatUserFactorModel(settingsFactorModel)}
                variant="outlined"
                sx={getSettingsChoiceChipSx(true)}
              />
            </Box>
          </SettingsRow>
          <SettingsRow
            label="Linked factor-data model"
            description="The selected vendor model or horizon sets the machine-learning context shown on this page."
          >
            <Box sx={{ display: 'grid', gap: 0.45, justifyItems: { xs: 'start', md: 'end' } }}>
              <Chip
                label={settingsActiveFactorDataModel || '—'}
                variant="outlined"
                sx={getSettingsChoiceChipSx(true, { monospace: true })}
              />
              {settingsActiveFactorDataModelDescription && (
                <Typography sx={{ fontSize: '0.69rem', color: 'text.secondary', lineHeight: 1.45 }}>
                  {settingsActiveFactorDataModelDescription}
                </Typography>
              )}
            </Box>
          </SettingsRow>
          <SettingsRow
            label="Where to change this"
            description="This section is informational only and does not save its own settings."
          >
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.5 }}>
              Update the provider, vendor model, or idio-vol source from Factor Data. This page will reflect those choices automatically.
            </Typography>
          </SettingsRow>
        </Box>
      </SettingsSection>

      <Paper variant="outlined" sx={{ ...settingsSectionCardSx, px: { xs: 1.4, md: 1.8 }, py: 0.15 }}>
        <IdioVolCompareTool
          factorModel={settingsFactorModel}
          factorDataModel={settingsActiveFactorDataModel}
          factorSnapshotDate={settingsLatestAiStatus?.factorSnapshotDate ?? null}
          idioVolSource={settingsIdioVolSource}
        />
      </Paper>


      <SettingsSection
        title="Recent live performance"
        description="Daily production-era performance rows for the active model, once the prediction target window has fully matured."
        infoKey="recent_live_performance"
        onInfoClick={handleOpenSettingsSectionInfo}
        paperSx={settingsSectionCardSx}
      >
        <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.35, display: 'grid', gap: 1 }}>
          <Tabs
            value={settingsAiPerformanceScope}
            onChange={(_event, nextValue) => {
              if (nextValue && nextValue !== settingsAiPerformanceScope) {
                setSettingsAiPerformanceLimit(SETTINGS_LIVE_PERFORMANCE_PAGE_SIZE);
                setSettingsAiPerformanceScope(nextValue);
              }
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 32,
              '& .MuiTabs-flexContainer': { gap: 0.4 },
              '& .MuiTab-root': {
                minHeight: 30,
                py: 0.35,
                px: 1.4,
                fontSize: '0.68rem',
                ...uppercaseLabel({
                  letterSpacing: '0.08em',
                }),
              },
            }}
          >
            {PERFORMANCE_SCOPE_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} disableRipple />
            ))}
          </Tabs>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.5 }}>
            {PERFORMANCE_SCOPE_DESCRIPTIONS[settingsAiPerformanceScope]}
          </Typography>
          {settingsAiModelMetadataLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                Loading live performance data...
              </Typography>
            </Box>
          )}
          {!settingsAiModelMetadataLoading && !settingsAiIdioSupported && (
            <Alert severity="info" variant="outlined">
              Live performance data is available when Barra is the active provider.
            </Alert>
          )}
          {!settingsAiModelMetadataLoading && settingsAiIdioSupported && !settingsAiModelMetadata?.has_recent_performance && settingsAiModelMetadata?.has_live_performance === false && (
            <Alert severity="info" variant="outlined">
              {settingsAiModelMetadata?.live_performance_pending_reason
                || 'Live performance is not yet available. The prediction target window has not fully matured.'}
            </Alert>
          )}
          {!settingsAiModelMetadataLoading && settingsAiIdioSupported && (settingsAiModelMetadata?.has_recent_performance || settingsAiModelMetadata?.has_live_performance) && (
            <>
              <RecentPerformanceChart
                liveRealizedRows={settingsAiModelMetadata?.recent_live_realized_rows || []}
                holdoutTestRows={
                  settingsAiPerformanceScope === PERFORMANCE_SCOPE_SYSTEM
                    ? (settingsAiModelMetadata?.recent_holdout_test_rows || [])
                    : []
                }
              />
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 2.4,
                  backgroundColor: (theme) => settingsMutedSurfaceColor(theme),
                }}
              >
                <Table size="small" aria-label="Recent live performance">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={uppercaseLabel({ fontSize: '0.67rem', fontWeight: 800, letterSpacing: '0.08em' })}>
                        Date
                      </TableCell>
                      <TableCell sx={uppercaseLabel({ fontSize: '0.67rem', fontWeight: 800, letterSpacing: '0.08em' })}>
                        Source
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={uppercaseLabel({ fontSize: '0.67rem', fontWeight: 800, letterSpacing: '0.08em' })}
                      >
                        RMSE
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={uppercaseLabel({ fontSize: '0.67rem', fontWeight: 800, letterSpacing: '0.08em' })}
                      >
                        Baseline RMSE
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={uppercaseLabel({ fontSize: '0.67rem', fontWeight: 800, letterSpacing: '0.08em' })}
                      >
                        Improvement %
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settingsRecentPerformanceTableRows.map((row) => (
                      <TableRow key={`${row.prediction_as_of_date}-${row.performance_source || 'live'}`}>
                        <TableCell sx={{ fontSize: '0.72rem', color: 'text.primary', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {formatSettingsDateTime(row.prediction_as_of_date, { includeTime: false })}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.66rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {row.performance_source === 'holdout_test' ? 'Held-out' : 'Live'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.72rem', color: 'text.primary' }}>
                          {formatAiMetricValue(row.model_rmse)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.72rem', color: 'text.primary' }}>
                          {formatAiMetricValue(row.baseline_rmse)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.72rem', ...getAiMetricDeltaTextSx(row.improvement_rmse) }}>
                          {formatAiMetricImprovementPct(row.improvement_rmse, row.baseline_rmse)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {settingsAiModelMetadata?.live_performance_has_more && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.25 }}>
                  <Button
                    variant="text"
                    size="small"
                    disabled={settingsAiModelMetadataLoading}
                    onClick={() => setSettingsAiPerformanceLimit((current) => Math.min(
                      (settingsAiModelMetadata?.live_performance_max_limit || 200),
                      current + (settingsAiModelMetadata?.live_performance_default_limit || SETTINGS_LIVE_PERFORMANCE_PAGE_SIZE),
                    ))}
                  >
                    Load older results
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </SettingsSection>
    </Box>
  );
  const renderRiskSettings = () => (
    <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto', display: 'grid', gap: 3 }}>
      {settingsPageIntro}
      <SettingsSection
        title="Risk calculation defaults"
        description="Choose the beta input source used by default risk calculations."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={settingsRowsContainerSx}>
          <SettingsRow
            label="Beta source"
            description="Choose which beta series should feed default risk calculations."
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {settingsBetaSourceOptions.map((option) => (
                <Tooltip
                  key={option.value}
                  title={option.kind === 'watchlist'
                    ? `${option.sourceName || option.label}${option.columnName ? ` (${option.columnName})` : ''}`
                    : option.value === 'factor'
                      ? 'Use factor-implied betas.'
                      : 'Use Yahoo betas.'}
                >
                  <Chip
                    clickable
                    label={option.label}
                    variant="outlined"
                    onClick={() => handleSelectSettingsBetaSource(option.value)}
                    sx={getSettingsChoiceChipSx(settingsBetaSource === option.value)}
                  />
                </Tooltip>
              ))}
            </Box>
          </SettingsRow>
          {!isBetaSourceAvailable(settingsAvailableBetaSources, settingsBetaSource) && (
            <SettingsRow
              label="Availability note"
              description="The selected source is not currently available for this account."
            >
              <Typography sx={{ fontSize: '0.7rem', color: 'warning.main', lineHeight: 1.5 }}>
                The app will fall back to Yahoo when these settings are saved.
              </Typography>
            </SettingsRow>
          )}
          {settingsBetaSourceOptions.length === 0 && (
            <SettingsRow
              label="Availability note"
              description="No beta-source choices are currently available."
            >
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.5 }}>
                Add a supported source before changing the default beta input.
              </Typography>
            </SettingsRow>
          )}
        </Box>
      </SettingsSection>

      <SettingsSection
        title="Live sync behavior"
        description="Control how risk, Watchlist, and Position Sizing react to new data."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={settingsRowsContainerSx}>
          <SettingsRow
            label="Price auto sync"
            description={syncControlState.riskAutoSyncTooltipText || 'Automatically refresh risk data when new price snapshots become available.'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
              {syncControlState.autoRefreshPending && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label={syncControlState.pendingAutoRefreshCount > 0
                    ? `Pending ${syncControlState.pendingAutoRefreshCount}`
                    : 'Pending update'}
                  sx={{ height: 26, fontSize: '0.68rem' }}
                />
              )}
              {renderSettingsSwitch(settingsAutoRefreshEnabled, handleToggleRiskAutoSync)}
            </Box>
          </SettingsRow>
          <SettingsRow
            label="Factor data auto-sync"
            description="Automatically apply new factor data when it becomes available. When off, show an update prompt on any tab."
          >
            {renderSettingsSwitch(settingsFactorDataAutoSyncEnabled, handleToggleFactorDataAutoSync, { disabled: !syncControlState.factorDataSyncPreferencesReady })}
          </SettingsRow>
          <SettingsRow
            label="Pos-sizing auto sync"
            description={syncControlState.posSizingAutoSyncTooltip || 'Automatically update the draft when its accepted portfolio context changes.'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
              {syncControlState.posSizingAutoSyncToggleBusy && <CircularProgress size={14} />}
              {renderSettingsSwitch(
                settingsPosSizingAutoSyncEnabled,
                handleTogglePosSizingAutoSync,
                {
                  disabled: syncControlState.posSizingAutoSyncToggleBusy,
                  statusLabel: syncControlState.posSizingAutoSyncToggleBusy
                    ? 'Updating'
                    : settingsPosSizingAutoSyncEnabled ? 'On' : 'Off',
                },
              )}
            </Box>
          </SettingsRow>
        </Box>
      </SettingsSection>

      <SettingsSection
        title="Session"
        description="Control when you're automatically signed out after a period of inactivity."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={settingsRowsContainerSx}>
          <SettingsRow
            label="Auto-logout"
            description="Sign out automatically after this much time without any activity in the app."
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {IDLE_LOGOUT_MINUTES_OPTIONS.map((option) => (
                <Chip
                  key={String(option.value)}
                  clickable
                  label={option.label}
                  variant="outlined"
                  onClick={() => handleSelectIdleLogoutMinutes(option.value)}
                  sx={getSettingsChoiceChipSx(idleLogoutMinutes === option.value)}
                />
              ))}
            </Box>
          </SettingsRow>
        </Box>
      </SettingsSection>

      <SettingsSection
        title="Sign-in methods"
        description="Connect the methods you can use to sign in to this Alphamizer account."
        paperSx={settingsSectionCardSx}
      >
        <SignInMethodsSettings
          user={user}
          providerIds={linkedProviderIds}
          onOpenPassword={handleResetPassword}
          onProvidersChanged={setLinkedProviderIds}
        />
      </SettingsSection>

      <SettingsSection
        title="Watchlist News"
        description="Tune what counts as 'recent' news for the notification dot next to each watchlist row."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={settingsRowsContainerSx}>
          <SettingsRow
            label="Recency window"
            description="Any story fresher than this lights up the red dot; opening the popover marks the recent stories as read (dot dulls until the next fresh story arrives)."
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {NEWS_RECENCY_HOURS_OPTIONS.map((option) => (
                <Chip
                  key={String(option.value)}
                  clickable
                  label={option.label}
                  variant="outlined"
                  onClick={() => handleSelectNewsRecencyHours(option.value)}
                  data-testid={`settings-news-recency-${option.value}`}
                  sx={getSettingsChoiceChipSx(newsRecencyHours === option.value)}
                />
              ))}
            </Box>
          </SettingsRow>
        </Box>
      </SettingsSection>

      <SettingsSection
        title="AI Deep Dive"
        description="Bring-your-own-key: pick your LLM provider and paste your own API key so the per-security Deep Dive can call it on your behalf."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={settingsRowsContainerSx}>
          <SettingsRow
            label="Provider API key"
            description="Stored only in this browser. Sent directly to your provider via our server; never saved on our servers."
          >
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setLlmSettingsOpen(true)}
                data-testid="settings-llm-configure-button"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Configure API key
              </Button>
            </Box>
          </SettingsRow>
          <SettingsRow
            label="Focus areas"
            description="Guide what your Deep Dive brief prioritizes. Selecting 'All' clears specifics; picking any specific option clears 'All'."
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {DEEP_DIVE_FOCUS_AREA_OPTIONS.map((option) => (
                <Chip
                  key={option.value}
                  clickable
                  label={option.label}
                  variant="outlined"
                  onClick={() => handleToggleDeepDiveFocusArea(option.value)}
                  data-testid={`settings-deep-dive-focus-${option.value}`}
                  sx={getSettingsChoiceChipSx(deepDiveFocusAreasSet.has(option.value))}
                />
              ))}
            </Box>
          </SettingsRow>
        </Box>
      </SettingsSection>
    </Box>
  );
  const renderIntegrationsSettings = () => (
    <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto', display: 'grid', gap: 3 }}>
      {settingsPageIntro}
      <SettingsSection
        title="Linked Excel Watchlist"
        description="Configure one organizational Microsoft 365 workbook. Connecting does not import or stage Watchlist changes."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.35 }}>
          <LinkedExcelWatchlistCard
            sessionKey={user?.uid || ''}
            callbackResult={m365CallbackResult}
            onGoToWatchlist={handleOpenLinkedExcelWatchlist}
            hideTitle
          />
        </Box>
      </SettingsSection>

      <SettingsSection
        title="Interactive Brokers"
        description="Manage Flex credentials, sync actions, and baseline or intraday portfolio imports from IBKR."
        paperSx={settingsSectionCardSx}
      >
        <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.35 }}>
          <IbkrConnectCard
            hideTitle
            onSyncSuccess={() => {
              window.dispatchEvent(new CustomEvent('imports-updated'));
              handleIntegrationsStatusChange();
            }}
            onStatusChange={handleIntegrationsStatusChange}
          />
        </Box>
      </SettingsSection>

      <SettingsSection
        title="Execution data source"
        description="Choose which provider should feed execution data into Performance workflows."
        paperSx={settingsSectionCardSx}
        action={(
          <Button
            size="small"
            variant="outlined"
            onClick={handleIntegrationsStatusChange}
            sx={{ ...pillButtonSx, minHeight: 30, px: 1.2 }}
          >
            Refresh
          </Button>
        )}
      >
        <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.35 }}>
          <ExecutionDataSourcePanel
            hideHeader
            liveCheckOnLoad={false}
            autoSelectOnLoad={false}
            selectedProviderId={userData?.selected_execution_provider_id || null}
            onSelectionChange={handleExecutionProviderSelectionChange}
            refreshToken={executionSourceRefreshToken}
          />
        </Box>
      </SettingsSection>
    </Box>
  );
  const renderMsiSettings = () => (
    <Box
      data-testid="security-resolution-settings-workspace"
      sx={{ width: '100%', maxWidth: 'none', mx: 0, display: 'grid', gap: 1.5 }}
    >
      <Box
        data-testid="security-resolution-settings-header"
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        {settingsPageIntro}
        <SecurityOperationProgress
          statusDoc={securityOperationStatus}
          onOpenRemovalReview={handleOpenWatchlistRemovalReview}
        />
      </Box>
      <Paper variant="outlined" sx={settingsSectionCardSx}>
        <WatchlistSecurityStatusTable
          resolutionCenter={displayMsiResolutionCenter}
          selectedFactorVendor={userData?.factor_model}
          onRefresh={onRefreshMsiResolutionCenter}
          focusTarget={msiResolutionFocusTarget}
        />
      </Paper>
    </Box>
  );

  const renderAdminSettings = () => (
    <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto', display: 'grid', gap: 3 }}>
      {settingsPageIntro}
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: (theme) => sharedBorderColor(theme),
        }}
      >
        <Tabs
          value={adminSettingsTab}
          onChange={(_event, value) => setAdminSettingsTab(value)}
          aria-label="Admin settings sections"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 34,
            '& .MuiTab-root': {
              minHeight: 34,
              px: 1.5,
              py: 0.5,
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 700,
            },
          }}
        >
          {ADMIN_SETTINGS_TAB_ITEMS.map((item) => (
            <Tab key={item.value} value={item.value} label={item.label} />
          ))}
        </Tabs>
      </Box>
      {adminSettingsTab === 'customer_organizations' ? (
        <SettingsSection
          title="Customer organizations"
          description="Manage access windows, capacity, status, and primary contacts."
          paperSx={settingsSectionCardSx}
        >
          <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.35 }}>
            <CustomerOrganizationsPanel
              organizations={customerOrganizations}
              loading={adminUsersLoading}
              error={adminUsersError}
              onRefresh={() => fetchAdminUsers({ force: true })}
            />
          </Box>
        </SettingsSection>
      ) : adminSettingsTab === 'team_members' ? (
        <SettingsSection
          title="Team members"
          description="Invite people, manage roles and active status, and grant or revoke admin access."
          paperSx={settingsSectionCardSx}
        >
          <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.35 }}>
            <AdminUsersPanel
              currentUid={user?.uid}
              currentEnv={currentEnv}
              users={adminUsers}
              invitations={adminInvitations}
              organizations={customerOrganizations}
              loading={adminUsersLoading}
              error={adminUsersError}
              onDismissError={() => setAdminUsersError('')}
              onRefresh={() => fetchAdminUsers({ force: true })}
            />
          </Box>
        </SettingsSection>
      ) : adminSettingsTab === 'system_credentials' ? (
        <SettingsSection
          title="System credentials"
          description="Keys this environment's background jobs use. Only system admins can replace or test them."
          paperSx={settingsSectionCardSx}
        >
          <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.35 }}>
            {isAdmin && <SystemCredentialsPanel />}
          </Box>
        </SettingsSection>
      ) : adminSettingsTab === 'market_calendar' ? (
        <SettingsSection
          title="Market calendar"
          description="Regenerate a month with AI, check what users see for it, and follow the job's runs and spend."
          paperSx={settingsSectionCardSx}
        >
          <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.35 }}>
            {isAdmin && (
              <MarketCalendarPanel onOpenSystemCredentials={() => setAdminSettingsTab('system_credentials')} />
            )}
          </Box>
        </SettingsSection>
      ) : (
        <FactorDataMonitoringPanel
          active={settingsOpen && settingsTab === 'admin' && isAdmin && adminSettingsTab === 'factor_data_monitoring'}
        />
      )}
    </Box>
  );
  const visibleSettingsTabItems = useMemo(
    () => SETTINGS_TAB_ITEMS.filter((item) => item.value !== 'admin' || isAdmin),
    [isAdmin],
  );

  const renderSettingsContent = () => {
    if (settingsTab === 'appearance') {
      return renderAppearanceSettings();
    }
    if (settingsTab === 'factor_data') {
      return renderFactorDataSettings();
    }
    if (settingsTab === 'machine_learning') {
      return renderMachineLearningSettings();
    }
    if (settingsTab === 'risk_settings') {
      return renderRiskSettings();
    }
    if (settingsTab === 'context_lineage') {
      return (
        <Box sx={{ width: '100%', maxWidth: 1120, mx: 'auto', display: 'grid', gap: 3 }}>
          {settingsPageIntro}
          <DataContextLineageSettings snapshot={contextLineage} showHeader={false} />
        </Box>
      );
    }
    if (settingsTab === 'msi') {
      return renderMsiSettings();
    }
    if (settingsTab === 'admin' && isAdmin) {
      return renderAdminSettings();
    }
    return renderIntegrationsSettings();
  };

  const sidebarWidth = getAppSidebarWidth(navigationCollapsed);

  return (
    <>
    <AppNavigationSidebar
      collapsed={navigationCollapsed}
      mobileOpen={mobileNavigationOpen}
      onMobileClose={() => setMobileNavigationOpen(false)}
      onCollapsedChange={onNavigationCollapsedChange}
      navItems={navItems}
      pathname={location.pathname}
      setupGuideTargetPath={setupGuideTargetPath}
      setupGuideTargetRef={setSetupGuideTargetEl}
      onSettings={handleOpenSettings}
      settingsActionRef={setSidebarSettingsEl}
      isProduction={isProduction}
      appMode={appMode}
      appearanceStyles={msiAppBarStyles}
      adminAccentColor={adminAccentColor}
    />
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      sx={{
        ...msiAppBarStyles.appBar,
        left: { xs: 0, md: `${sidebarWidth}px` },
        width: { xs: '100%', md: `calc(100% - ${sidebarWidth}px)` },
        height: APP_SHELL_DIMENSIONS.utilityBarHeight,
        backgroundColor: 'background.paper',
        backgroundImage: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        transition: (theme) => theme.transitions.create(['left', 'width'], {
          duration: theme.transitions.duration.shorter,
        }),
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: `${APP_SHELL_DIMENSIONS.utilityBarHeight}px`,
          height: `${APP_SHELL_DIMENSIONS.utilityBarHeight}px`,
          px: { xs: 1, md: 1.25 },
          gap: 0,
          ...msiAppBarStyles.toolbar,
          background: 'transparent',
          border: 0,
          borderRadius: 0,
          boxShadow: 'none',
        }}
      >
        <IconButton
          ref={setNavigationMenuButtonEl}
          onClick={() => setMobileNavigationOpen(true)}
          aria-label="Open navigation"
          sx={{ display: { xs: 'inline-flex', md: 'none' }, ...utilityIconSx }}
        >
          <MenuRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box sx={appBarHeaderSlotSx(activeAppBarView)}>
          {activeAppBarView && (
            <DashboardPageHeader activeView={activeAppBarView} />
          )}
        </Box>
        {activeAppBarView && activeAppBarView !== 'watchlist' && user
          && (appBarFactorUpdateProgress || securityOperationStatus?.found) && (
          <SecurityOperationProgress
            statusDoc={securityOperationStatus}
            contextUpdate={appBarFactorUpdateProgress}
            compact
            onOpenRemovalReview={handleOpenWatchlistRemovalReview}
          />
        )}
        <Box
          id={DASHBOARD_TOOLBAR_ACTIONS_ID}
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            height: '100%',
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'flex-start',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        />
        {activeAppBarView && (
          <Divider orientation="vertical" flexItem sx={appBarDividerSx} />
        )}
        {user && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
              <NotificationBell
                user={user}
                watchlistPublicationState={watchlistPublicationState}
                watchlistContextStatus={contextLineage?.userUid === user?.uid
                  ? contextLineage?.watchlistActual?.contextStatus : null}
                onReviewWatchlistContext={() => {
                  setSettingsOpen(false);
                  window.dispatchEvent(new CustomEvent(FACTOR_DATA_REFRESH_REQUEST_EVENT));
                  return true;
                }}
              />
              <SecurityResolutionHint
                open={showSecurityResolutionHint}
                anchorEl={securityResolutionHintAnchorEl}
                onOpen={handleOpenSecurityResolutionFromHint}
                onDismiss={dismissSecurityResolutionHint}
              />
              <SetupStepHint
                step={setupGuideStep}
                open={showSetupStepHint}
                anchorEl={useCompactSettingsNavigation ? navigationMenuButtonEl : setupGuideTargetEl}
                onOpen={() => navigate(setupGuideTargetPath)}
                onDismiss={handleDismissSetupStepHint}
              />
              {isAdmin && (
                <Tooltip title={ADMIN_TOOLTIP_TEXT} arrow>
                  {/* Visual only -- the admin surface is the User Settings > Admin tab. */}
                  <Box
                    component="span"
                    sx={{
                      display: { xs: 'none', sm: 'inline-flex' },
                      justifyContent: 'center',
                      alignItems: 'center',
                      ...appBarActionButtonSx,
                      backgroundColor: (theme) => alpha(adminAccentColor, theme.palette.mode === 'dark' ? 0.16 : 0.08),
                      color: adminAccentColor,
                      cursor: 'default',
                    }}
                  >
                    <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Tooltip>
              )}
              <Button
                color="inherit"
                onClick={handleProfileMenuOpen}
                endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
                sx={{
                  ...appBarActionButtonSx,
                  width: 'auto',
                  px: 1.25,
                  textTransform: 'none',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  maxWidth: { xs: 104, sm: 150, md: 240 },
                  '& .MuiButton-endIcon': {
                    ml: 0.2,
                  },
                }}
              >
                <Box component="span" className="truncate">
                  {userData?.name || user.email}
                </Box>
              </Button>
            </Box>

            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={handleProfileMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 180,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: (theme) => sharedBorderColor(theme),
                  boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 18px 36px rgba(0, 0, 0, 0.38)'
                    : '0 18px 36px rgba(15, 23, 42, 0.12)',
                  ...msiAppBarStyles.menuPaper,
                },
              }}
            >
              {profileMenuItems.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={() => {
                    if (item.action) item.action();
                    else handleProfileMenuClose();
                  }}
                  sx={{
                    fontSize: '0.8rem',
                    minHeight: 34,
                  }}
                >
                  <Box component="span" sx={{ color: item.color === 'error' ? 'error.main' : 'inherit' }}>
                    {item.label}
                  </Box>
                </MenuItem>
              ))}
            </Menu>

            {showResetPasswordModal && (
              <ResetPasswordModal
                open={showResetPasswordModal}
                onClose={() => setShowResetPasswordModal(false)}
                userEmail={user?.email}
                handleResetPasswordModalClose={handleResetPasswordModalClose}
                expectedUid={user?.uid}
                isPasswordConnected={linkedProviderIds.includes('password')}
                onPasswordUpdated={setLinkedProviderIds}
              />
            )}

            <Dialog
              open={uploadModalOpen}
              onClose={handleCloseUploadModal}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: uploadModalMode === 'review'
                  ? dialogPaperSx
                  : [
                    dialogPaperSx,
                    {
                      alignSelf: 'flex-start',
                      mt: '8vh',
                      height: '76vh',
                      maxHeight: '76vh',
                      width: '68vw',
                      maxWidth: 860,
                    },
                  ],
              }}
            >
              <DialogTitle sx={{ px: 2, py: 1.5 }}>
                <Box className="flex items-center justify-between gap-2 flex-wrap">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {uploadModalMode !== 'review' && <HistoryOutlinedIcon fontSize="small" color="action" />}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {uploadModalMode === 'review' ? 'Portfolio Import Review' : 'Portfolio History'}
                    </Typography>
                  </Box>
                  {uploadModalMode !== 'review' && (
                    <Tooltip
                      title={uploadContext.watchlistIsReady === true
                        ? 'Upload a portfolio file'
                        : uploadContext.watchlistIsReady == null
                          ? 'Checking for watchlist…'
                          : 'Upload a watchlist before importing a portfolio.'}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          ml: 'auto',
                          opacity: uploadContext.watchlistIsReady === true ? 1 : 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            pointerEvents: uploadContext.watchlistIsReady === true ? 'auto' : 'none',
                          }}
                          onClickCapture={() => {
                            window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_INTERACTION_EVENT));
                          }}
                        >
                          <PortfolioUploader
                            acceptTransferredFile
                            selectedFactorModel={uploadContext.selectedFactorModel}
                            serverNormalVol={uploadContext.serverNormalVol}
                            serverMaxPosSize={uploadContext.serverMaxPosSize}
                            aum={uploadContext.aum}
                            default_target_vol_pct={uploadContext.defaultTargetVolPct}
                            default_target_vol_dollar={uploadContext.defaultTargetVolDollar}
                            default_scaler={uploadContext.defaultScaler}
                            compact
                            onUploadSuccess={() => {
                              window.dispatchEvent(new CustomEvent('imports-updated'));
                            }}
                          />
                        </Box>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </DialogTitle>
              <DialogContent dividers sx={{ px: 2, py: 1.5, minHeight: 0, overflowY: 'auto' }}>
                <Box
                  className={uploadModalMode === 'review'
                    ? 'flex flex-col min-h-0'
                    : 'h-full flex flex-col min-h-0'}
                >
                  <PortfolioIngestPanel
                    userUid={user?.uid || null}
                    selectedFactorModel={uploadContext.selectedFactorModel}
                    serverNormalVol={uploadContext.serverNormalVol}
                    serverMaxPosSize={uploadContext.serverMaxPosSize}
                    aum={uploadContext.aum}
                    default_target_vol_pct={uploadContext.defaultTargetVolPct}
                    default_target_vol_dollar={uploadContext.defaultTargetVolDollar}
                    default_scaler={uploadContext.defaultScaler}
                    watchlistIsReady={uploadContext.watchlistIsReady}
                    variant={uploadModalMode === 'review' ? 'review' : 'history'}
                    showRecentButton={false}
                    reviewImportRun={uploadReviewRun}
                    onPendingResolved={handleCloseUploadModal}
                    onGuideInteraction={() => {
                      window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_INTERACTION_EVENT));
                    }}
                    onImportApplied={() => dispatchPortfolioImportApplied({ userUid: user?.uid })}
                  />
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={handleCloseUploadModal} sx={pillButtonSx}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            <FactorContextReviewNotice
              key={securityOperationSessionScopeToken}
              status={securityOperationStatus}
              publicationState={watchlistPublicationState}
              sessionScopeToken={securityOperationSessionScopeToken}
              suppressed={settingsOpen}
              onReview={handleOpenWatchlistRemovalReview}
            />
            <Dialog
              open={settingsOpen}
              onClose={handleCloseSettings}
              fullScreen
              disableEscapeKeyDown={settingsSaving}
              PaperProps={{ sx: settingsWorkspacePaperSx }}
            >
              <Box
                data-testid="settings-shell"
                sx={{
                  height: '100%',
                  width: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    borderBottom: '1px solid',
                    borderColor: (theme) => sharedBorderColor(theme),
                    backgroundColor: 'rgb(var(--c-deep) / 0.94)',
                    backdropFilter: 'blur(16px)',
                    ...msiAppBarStyles.settingsHeader,
                  }}
                >
                  <Box
                    sx={{
                      px: { xs: 1.6, md: 2.4 },
                      py: 1.2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <IconButton
                      onClick={handleCloseSettings}
                      disabled={settingsSaving}
                      aria-label="Close settings"
                      sx={{
                        ...utilityIconSx,
                        ...msiAppBarStyles.utilityControl,
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                      }}
                    >
                      <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                      <Box sx={{ display: 'grid', gap: 0.12 }}>
                        <Typography sx={{ ...SETTINGS_DISPLAY_TITLE_SX, fontSize: '0.98rem', color: 'inherit' }}>
                          User Settings
                        </Typography>
                        <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.66rem', color: 'inherit', opacity: 0.68 }}>
                          Workspace preferences, data sources, and account controls
                        </Typography>
                      </Box>
                      {!settingsSaving && settingsHasPendingChanges && (
                        <Chip
                          size="small"
                          label={settingsPendingSummary}
                          variant="outlined"
                          sx={{
                            height: 24,
                            borderRadius: 999,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.56 : 0.24),
                            backgroundColor: (theme) => theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.main, 0.2)
                              : alpha(theme.palette.primary.main, 0.08),
                            '& .MuiChip-label': {
                              px: 1,
                            },
                          }}
                        />
                      )}
                      {settingsSaving && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
                          <CircularProgress size={12} thickness={5} />
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'inherit' }}>
                            {normalizeUserFactorModel(settingsFactorModel) !== normalizeUserFactorModel(settingsConfirmedState.factorModel)
                              ? `Preparing your switch to ${formatUserFactorModel(settingsFactorModel)}…`
                              : 'Saving changes...'}
                          </Typography>
                          {settingsSaveStepLabel && (
                            <Typography sx={{ fontSize: '0.72rem', color: 'inherit', opacity: 0.72 }}>
                              {settingsSaveStepLabel}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      {settingsHasPendingChanges && (
                        <Button variant="contained" onClick={handleSaveSettings} disabled={settingsSaving}>
                          Save changes
                        </Button>
                      )}
                      {settingsHasPendingChanges && !settingsSaving && (
                        <Button
                          onClick={handleDiscardSettingsChanges}
                          sx={{ ...pillButtonSx, minHeight: 32, px: 1.25 }}
                        >
                          Discard changes
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {settingsSaveErrors.length > 0 && (
                    <Alert
                      severity="error"
                      variant="outlined"
                      sx={{
                        mx: { xs: 1.6, md: 2.4 },
                        mb: 1.2,
                        borderRadius: 2.5,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: 'text.primary' }}>
                        Some changes could not be applied.
                      </Typography>
                      <Typography sx={{ mt: 0.25, fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.5 }}>
                        Failed groups were reverted to their last saved values.
                      </Typography>
                      <Box sx={{ mt: 0.8, display: 'grid', gap: 0.35 }}>
                        {settingsSaveErrors.map((error) => (
                          <Typography key={error.groupKey} sx={{ fontSize: '0.72rem', lineHeight: 1.5 }}>
                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {error.label}
                              {': '}
                            </Box>
                            {error.message}
                          </Typography>
                        ))}
                      </Box>
                    </Alert>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    width: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    pointerEvents: settingsSaving ? 'none' : 'auto',
                    opacity: settingsSaving ? 0.76 : 1,
                    transition: 'opacity 120ms ease',
                  }}
                >
                  <Box
                    data-testid="settings-navigation"
                    sx={{
                      width: { xs: '100%', md: 220 },
                      maxWidth: '100%',
                      flexShrink: 0,
                      borderRight: { xs: 0, md: '1px solid' },
                      borderBottom: { xs: '1px solid', md: 0 },
                      borderColor: (theme) => sharedBorderColor(theme),
                      px: { xs: 1.2, md: 1.1 },
                      py: { xs: 0.9, md: 1.4 },
                      overflowX: { xs: 'auto', md: 'hidden' },
                      overflowY: { xs: 'hidden', md: 'auto' },
                      ...msiAppBarStyles.settingsNavigation,
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        gap: 0.8,
                        minHeight: 38,
                        px: 0.75,
                        mb: 1.25,
                        color: 'inherit',
                      }}
                    >
                      <SettingsOutlinedIcon sx={{ fontSize: 18, opacity: 0.72 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, lineHeight: 1.2 }}>
                          Settings
                        </Typography>
                        <Typography sx={{ mt: 0.15, fontSize: '0.61rem', lineHeight: 1.2, opacity: 0.62 }}>
                          Customize your workspace
                        </Typography>
                      </Box>
                    </Box>
                    <Tabs
                      orientation={useCompactSettingsNavigation ? 'horizontal' : 'vertical'}
                      value={settingsTab}
                      onChange={(_, value) => {
                        setSettingsTab(value);
                        if (value !== 'integrations') {
                          setM365CallbackResult(null);
                        }
                      }}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      aria-label="User settings sections"
                      sx={settingsTopTabsSx}
                    >
                      {visibleSettingsTabItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Tab
                            key={item.value}
                            value={item.value}
                            icon={<Icon sx={{ fontSize: 16 }} />}
                            iconPosition="start"
                            label={item.label}
                          />
                        );
                      })}
                    </Tabs>
                  </Box>
                  <Box
                    data-testid="settings-content"
                    ref={settingsContentRef}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      width: '100%',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      px: { xs: 1.6, md: 2.4 },
                      py: { xs: 2.2, md: 3 },
                    }}
                  >
                    {renderSettingsContent()}
                  </Box>
                </Box>
              </Box>
            </Dialog>
            <Popover
              open={Boolean(settingsInfoAnchorEl)}
              anchorEl={settingsInfoAnchorEl}
              onClose={handleCloseSettingsSectionInfo}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  mt: 0.5,
                  p: 1,
                  maxWidth: 300,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.44 : 0.86),
                },
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {settingsInfoContent}
              </Typography>
            </Popover>

            <FactorVendorSwitchConfirmDialog
              question={vendorSwitchQuestion}
              onAnswer={(answer) => vendorSwitchQuestion?.resolve(answer)}
            />

            <Snackbar
              open={settingsSnackbar.open}
              autoHideDuration={4000}
              onClose={() => setSettingsSnackbar((current) => ({ ...current, open: false }))}
            >
              <Alert
                severity={settingsSnackbar.severity}
                onClose={() => setSettingsSnackbar((current) => ({ ...current, open: false }))}
                sx={{ width: '100%' }}
              >
                {settingsSnackbar.message}
              </Alert>
            </Snackbar>

            <LlmSettingsDialog
              open={llmSettingsOpen}
              onClose={() => setLlmSettingsOpen(false)}
            />
          </>
        )}
      </Toolbar>
    </AppBar>
    </>
  );
};

AppBarComponent.propTypes = {
  navigationCollapsed: PropTypes.bool,
  onNavigationCollapsedChange: PropTypes.func,
  user: PropTypes.object,
  userData: PropTypes.object,
  onUserDataChange: PropTypes.func,
  compactMode: PropTypes.bool.isRequired,
  toggleCompactMode: PropTypes.func.isRequired,
  msiResolutionCenter: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      status: PropTypes.string,
      message: PropTypes.string,
      status_counts: PropTypes.object,
      actionable_count: PropTypes.number,
      row_count_total: PropTypes.number,
      rows_truncated: PropTypes.bool,
      rows: PropTypes.array,
      actionable_rows: PropTypes.array,
      review_rows: PropTypes.array,
      editable_rows: PropTypes.array,
    }),
  }),
  onEnsureMsiResolutionCenterLoaded: PropTypes.func,
  onRefreshMsiResolutionCenter: PropTypes.func,
  securityOperationStatus: PropTypes.object,
  watchlistPublicationState: PropTypes.object,
  themeId: PropTypes.string.isRequired,
  setThemeId: PropTypes.func.isRequired,
  colorOverrides: PropTypes.object,
  setColorOverride: PropTypes.func.isRequired,
  resetColorOverride: PropTypes.func.isRequired,
  resetAllColorOverrides: PropTypes.func.isRequired,
  idleLogoutMinutes: PropTypes.oneOf([5, 10, 30, 60, null]),
  setIdleLogoutMinutes: PropTypes.func.isRequired,
  newsRecencyHours: PropTypes.oneOf([1, 2, 6, 24, 48]),
  setNewsRecencyHours: PropTypes.func,
};

export default AppBarComponent;
